/**
 * Cart Slider - Core JavaScript
 * A premium cart drawer for Shopify
 * 
 * Features:
 * - Theme-agnostic cart drawer
 * - Tiered offers with progress bar
 * - Free product rewards (single auto-add or multi-select modal)
 * - Upsell carousel
 * - Coupon code handling
 * - Smooth animations & confetti
 */

(function () {
  'use strict';

  // Prevent double initialization
  if (window.CartSliderInitialized) return;
  window.CartSliderInitialized = true;

  // SAFETY: Global error handler to reset body scroll if cart JS crashes
  window.addEventListener('error', function () {
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.touchAction = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.style.height = '';
    document.body.classList.remove('cart-slider-open');

    // Also clear scroll-lock library attributes
    document.body.removeAttribute('data-scroll-lock-locked');
    document.body.removeAttribute('data-scroll-lock-saved-overflow-y-property');
    document.body.removeAttribute('data-scroll-lock-saved-inline-overflow-property');
    document.body.removeAttribute('data-scroll-lock-saved-inline-overflow-y-property');
    document.documentElement.removeAttribute('data-scroll-locked');

    // Call theme's scroll-lock library to unlock if available
    if (window.themeVendor?.ScrollLock) {
      try {
        window.themeVendor.ScrollLock.clearQueueScrollLocks();
        window.themeVendor.ScrollLock.enablePageScroll();
      } catch (e) { /* ignore */ }
    }
  });

  // ============================================
  // CONFIGURATION
  // ============================================
  const CONFIG = {
    selectors: {
      slider: '[data-cart-slider]',
      overlay: '[data-cart-overlay]',
      closeBtn: '[data-cart-close]',
      cartCount: '[data-cart-count]',
      cartSubtotal: '[data-cart-subtotal]',
      cartItems: '[data-cart-items]',
      cartEmpty: '[data-cart-empty]',
      tierFill: '[data-tier-fill]',
      tierMessage: '[data-tier-message]',
      tierMarker: '[data-tier-marker]',
      tierBadge: '[data-tier-badge]',
      upsellAdd: '[data-upsell-add]',
      qtyIncrease: '[data-qty-increase]',
      qtyDecrease: '[data-qty-decrease]',
      qtyInput: '[data-qty-input]',
      removeItem: '[data-remove-item]',
      cartItem: '[data-cart-item]',
      rewardModal: '[data-reward-modal]',
      rewardModalBody: '[data-reward-modal-body]',
      rewardConfirm: '[data-reward-confirm]',
      rewardModalClose: '[data-reward-modal-close]',
      confetti: '[data-confetti]',

      offerCheckbox: '[data-offer-checkbox]',
      discountedOffer: '[data-discounted-offer]',
      upsellsTrack: '[data-upsells-track]',
      upsellPrev: '[data-upsell-prev]',
      upsellNext: '[data-upsell-next]',
      upsellDots: '[data-upsell-dots]',
      configData: '[data-cart-slider-config]',
    },
    classes: {
      open: 'is-open',
      active: 'is-active',
      loading: 'is-loading',
      unlocked: 'tier-marker--unlocked',
      removing: 'cart-item--removing',
      selected: 'is-selected',
      added: 'is-added',
    },
    api: {
      cart: '/cart.js',
      add: '/cart/add.js',
      change: '/cart/change.js',
      update: '/cart/update.js',
      clear: '/cart/clear.js',
    }
  };

  // ============================================
  // STATE
  // ============================================
  let state = {
    cart: null,
    config: null,
    isOpen: false,
    isLoading: false,
    internalUpdateCount: 0, // Counter to prevent self-triggering loops (handles overlapping requests)
    unlockedTiers: new Set(),
    selectedReward: null,
    pendingRewardTier: null,
    variantDataCache: {}, // Cache for variant compare_at_price data from Product API
  };

  // Try to load cache from sessionStorage
  try {
    const cached = sessionStorage.getItem('cartSliderVariantCache');
    if (cached) {
      state.variantDataCache = JSON.parse(cached);
    }
  } catch (e) {
    // Ignore storage errors
  }

  // ============================================
  // DOM ELEMENTS
  // ============================================
  let elements = {};

  // ============================================
  // UTILITY FUNCTIONS
  // ============================================

  /**
   * Escape HTML special characters to prevent XSS
   * Uses string replacement instead of DOM creation for performance
   */
  function escapeHtml(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * Get the locked minimum quantity for a cart item (0 if not locked)
   * Matches by product ID against the config list
   */
  function getLockedQty(item) {
    const lockedProducts = state.config?.quantityLockedProducts;
    if (!lockedProducts || !lockedProducts.length) return 0;
    const match = lockedProducts.find(p => p.productId === item.product_id);
    return match ? (match.lockedQty || 1) : 0;
  }

  /**
   * Get the total quantity of ALL variants of a product in the cart
   */
  function getProductTotalQty(productId) {
    if (!state.cart?.items) return 0;
    return state.cart.items
      .filter(item => item.product_id === productId)
      .reduce((sum, item) => sum + item.quantity, 0);
  }

  /**
   * Remove ALL variants of a product from the cart
   */
  async function removeAllVariantsOfProduct(productId) {
    if (!state.cart?.items) return;
    const variantKeys = state.cart.items
      .filter(item => item.product_id === productId)
      .map(item => item.key);

    // Build updates object: set all to 0
    const updates = {};
    variantKeys.forEach(key => { updates[key] = 0; });

    setLoading(true);
    state.internalUpdateCount++;
    try {
      const response = await fetch(CONFIG.api.update, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });
      if (!response.ok) throw new Error('Failed to remove items');
      const updatedCart = await response.json();
      state.cart = updatedCart;
      updateCartUI();
      updateTierProgress();
      checkTierRewards();
      dispatchCartEvent();
    } catch (error) {
      console.error('Remove all variants error:', error);
      await fetchCart();
    } finally {
      state.internalUpdateCount = Math.max(0, state.internalUpdateCount - 1);
      setLoading(false);
    }
  }

  /**
   * Get Shopify CDN sized image URL
   * @param {string} src - Original image URL
   * @param {string} size - Size string like '160x', '320x160', etc.
   */
  function getSizedImageUrl(src, size) {
    if (!src) return '';

    // Handle Shopify CDN URLs
    if (src.includes('cdn.shopify.com')) {
      // Remove any existing size modifier
      const cleanSrc = src.replace(/_(\d+x\d*|\d*x\d+)\./, '.');
      // Insert the size before the file extension
      return cleanSrc.replace(/\.(\w+)(\?.*)?$/, `_${size}.$1$2`);
    }

    // Return original for non-Shopify URLs
    return src;
  }

  // ============================================
  // INITIALIZATION
  // ============================================
  function init() {

    cacheElements();
    loadConfig();
    bindEvents();
    interceptAddToCart();
    interceptCartLinks();

    // Initial cart fetch (fetchCart handles all UI updates internally)
    fetchCart();

    // Expose a global refresh method for external apps to call
    window.CartSliderRefresh = () => {
      fetchCart();
    };

    // Dispatch global cart event for 3rd party apps (Koalla Bundles, etc.)
    window.dispatchCartEvent = dispatchCartEvent;
  }

  function cacheElements() {
    const { selectors } = CONFIG;

    // Always query fresh to handle DOM changes from Turbo/AJAX navigation
    elements = {
      slider: document.querySelector(selectors.slider),
      overlay: document.querySelector(selectors.overlay),
      closeBtns: document.querySelectorAll(selectors.closeBtn),
      cartCounts: document.querySelectorAll(selectors.cartCount),
      cartSubtotal: document.querySelector(selectors.cartSubtotal),
      cartItems: document.querySelector(selectors.cartItems),
      tierFill: document.querySelector(selectors.tierFill),
      tierMessage: document.querySelector(selectors.tierMessage),
      tierMarkers: document.querySelectorAll(selectors.tierMarker),
      tierBadge: document.querySelector(selectors.tierBadge),
      rewardModal: document.querySelector(selectors.rewardModal),
      rewardModalBody: document.querySelector(selectors.rewardModalBody),
      rewardConfirm: document.querySelector(selectors.rewardConfirm),
      confetti: document.querySelector(selectors.confetti),

      upsellsTrack: document.querySelector(selectors.upsellsTrack),
      upsellPrev: document.querySelector(selectors.upsellPrev),
      upsellNext: document.querySelector(selectors.upsellNext),
      upsellDots: document.querySelector(selectors.upsellDots),
      discountBanner: document.querySelector('[data-tier-discount-banner]'),
      estimatedTotal: document.querySelector('[data-estimated-total]'),
      estimatedDiscount: document.querySelector('[data-estimated-discount]'),
    };
  }

  /**
   * Dispatch cart update event for 3rd party apps (Koalla Bundles, etc.)
   * Lightweight - only dispatches when cart data is available
   */
  function dispatchCartEvent() {
    if (!state.cart) return;

    // Dispatch standard Shopify cart event
    document.dispatchEvent(new CustomEvent('cart:updated', {
      detail: { cart: state.cart },
      bubbles: true
    }));

  }

  function loadConfig() {
    const configEl = document.querySelector(CONFIG.selectors.configData);
    if (configEl) {
      try {
        state.config = JSON.parse(configEl.textContent);
      } catch (e) {
        console.error('Failed to parse cart slider config:', e);
        state.config = { tiers: [], animations: { confetti: true } };
      }
    }
  }

  // ============================================
  // EVENT BINDING
  // ============================================
  function bindEvents() {
    // Open/Close
    elements.overlay?.addEventListener('click', close);
    elements.closeBtns?.forEach(btn => btn.addEventListener('click', close));

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && state.isOpen) close();
    });

    // Cart items container - delegated events
    elements.cartItems?.addEventListener('click', handleCartItemClick);
    elements.cartItems?.addEventListener('change', handleQuantityChange);

    // Upsell buttons
    document.addEventListener('click', handleUpsellClick);

    // FBT buttons
    document.addEventListener('click', handleFbtClick);

    // Upsell variant selector
    document.addEventListener('change', handleUpsellVariantChange);



    // Discounted offers checkboxes
    document.querySelectorAll(CONFIG.selectors.offerCheckbox).forEach(checkbox => {
      checkbox.addEventListener('change', handleDiscountedOfferChange);
    });

    // Reward modal
    elements.rewardModalBody?.addEventListener('click', handleRewardSelect);
    elements.rewardConfirm?.addEventListener('click', confirmRewardSelection);
    document.querySelectorAll(CONFIG.selectors.rewardModalClose).forEach(btn => {
      btn.addEventListener('click', closeRewardModal);
    });

    // Upsell carousel navigation
    elements.upsellPrev?.addEventListener('click', () => scrollUpsells(-1));
    elements.upsellNext?.addEventListener('click', () => scrollUpsells(1));

    // Price Breakdown toggle (delegated – panel lives in footer which is always in DOM)
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-price-breakdown-trigger]');
      if (trigger) {
        e.stopPropagation();
        togglePriceBreakdown();
        return;
      }
      const closeBtn = e.target.closest('[data-price-breakdown-close]');
      if (closeBtn) {
        closePriceBreakdown();
        return;
      }
      // Click outside the breakdown panel — close it
      const panel = document.querySelector('[data-price-breakdown]');
      if (panel && panel.style.display !== 'none' && !panel.contains(e.target)) {
        closePriceBreakdown();
      }
    });
  }

  // ============================================
  // CART ITEM EVENT HANDLERS
  // ============================================

  /**
   * Handle clicks on cart item elements (quantity buttons, remove button)
   * Uses DIRECT updateItemQuantity calls (not debounced) to avoid race conditions
   * with fetchCart() that can reset input values.
   * Quantity lock is enforced at the PRODUCT level (sum of all variants).
   */
  function handleCartItemClick(e) {
    const target = e.target;
    const cartItem = target.closest('[data-cart-item]');
    if (!cartItem) return;

    const lineKey = cartItem.dataset.lineKey;
    if (!lineKey) return;

    const cartItemData = state.cart?.items?.find(i => i.key === lineKey);
    const lockedQty = cartItemData ? getLockedQty(cartItemData) : 0;

    // Handle remove button
    if (target.closest('[data-remove-item]')) {
      e.preventDefault();
      if (lockedQty > 0 && cartItemData) {
        // Check if removing this variant would drop product total below lock
        const productTotal = getProductTotalQty(cartItemData.product_id);
        const remainingAfterRemove = productTotal - cartItemData.quantity;
        if (remainingAfterRemove < lockedQty) {
          // Would drop below minimum — remove ALL variants of this product
          // Add removing animation to all variants
          document.querySelectorAll(`[data-cart-item]`).forEach(el => {
            const elData = state.cart?.items?.find(i => i.key === el.dataset.lineKey);
            if (elData && elData.product_id === cartItemData.product_id) {
              el.classList.add(CONFIG.classes.removing);
            }
          });
          removeAllVariantsOfProduct(cartItemData.product_id);
          return;
        }
      }
      cartItem.classList.add(CONFIG.classes.removing);
      removeItem(lineKey);
      return;
    }

    // Handle quantity decrease
    if (target.closest('[data-qty-decrease]')) {
      e.preventDefault();
      const input = cartItem.querySelector('[data-qty-input]');
      const currentQty = parseInt(input?.value || '1', 10);

      if (lockedQty > 0 && cartItemData) {
        const productTotal = getProductTotalQty(cartItemData.product_id);
        if (currentQty <= 1) {
          // Trying to remove this variant (qty → 0)
          const remainingAfterRemove = productTotal - currentQty;
          if (remainingAfterRemove < lockedQty) {
            // Would drop below minimum — remove ALL variants
            document.querySelectorAll(`[data-cart-item]`).forEach(el => {
              const elData = state.cart?.items?.find(i => i.key === el.dataset.lineKey);
              if (elData && elData.product_id === cartItemData.product_id) {
                el.classList.add(CONFIG.classes.removing);
              }
            });
            removeAllVariantsOfProduct(cartItemData.product_id);
            return;
          }
        } else {
          // Trying to decrease this variant by 1
          if (productTotal - 1 < lockedQty) {
            return; // Would drop below minimum — block decrease
          }
        }
      }

      if (currentQty <= 1) {
        cartItem.classList.add(CONFIG.classes.removing);
        removeItem(lineKey);
      } else {
        updateItemQuantity(lineKey, currentQty - 1);
      }
      return;
    }

    // Handle quantity increase
    if (target.closest('[data-qty-increase]')) {
      e.preventDefault();
      const input = cartItem.querySelector('[data-qty-input]');
      const currentQty = parseInt(input?.value || '1', 10);
      updateItemQuantity(lineKey, currentQty + 1);
      return;
    }
  }

  /**
   * Handle quantity input changes (typing in the field)
   * Uses debouncing since typing can trigger many rapid change events.
   * Quantity lock is enforced at the PRODUCT level (sum of all variants).
   */
  function handleQuantityChange(e) {
    const input = e.target.closest('[data-qty-input]');
    if (!input) return;

    const cartItem = input.closest('[data-cart-item]');
    if (!cartItem) return;

    const lineKey = cartItem.dataset.lineKey;
    if (!lineKey) return;

    const newQty = parseInt(input.value, 10);
    if (isNaN(newQty) || newQty < 0) {
      input.value = 1;
      return;
    }

    const cartItemData = state.cart?.items?.find(i => i.key === lineKey);
    const lockedQty = cartItemData ? getLockedQty(cartItemData) : 0;

    if (lockedQty > 0 && cartItemData) {
      const productTotal = getProductTotalQty(cartItemData.product_id);
      const diff = newQty - cartItemData.quantity;
      const newProductTotal = productTotal + diff;

      if (newQty === 0) {
        // Removing this variant — check if remaining is below lock
        if (newProductTotal < lockedQty) {
          // Remove ALL variants of this product
          document.querySelectorAll(`[data-cart-item]`).forEach(el => {
            const elData = state.cart?.items?.find(i => i.key === el.dataset.lineKey);
            if (elData && elData.product_id === cartItemData.product_id) {
              el.classList.add(CONFIG.classes.removing);
            }
          });
          removeAllVariantsOfProduct(cartItemData.product_id);
          return;
        }
      } else if (newProductTotal < lockedQty) {
        // Would drop below minimum — reset input
        input.value = cartItemData.quantity;
        return;
      }
    }

    if (newQty === 0) {
      cartItem.classList.add(CONFIG.classes.removing);
      removeItem(lineKey);
    } else {
      debouncedUpdateQuantity(lineKey, newQty);
    }
  }

  // ============================================
  // CART DRAWER OPEN/CLOSE
  // ============================================

  // Store scroll position to restore after closing
  let savedScrollPosition = 0;

  function open(skipFetch = false) {
    // Re-cache elements to ensure we have fresh references
    cacheElements();

    // Rebind events on cart items container
    if (elements.cartItems) {
      elements.cartItems.removeEventListener('click', handleCartItemClick);
      elements.cartItems.removeEventListener('change', handleQuantityChange);
      elements.cartItems.addEventListener('click', handleCartItemClick);
      elements.cartItems.addEventListener('change', handleQuantityChange);
    }

    // Save scroll position before locking body
    savedScrollPosition = window.pageYOffset || document.documentElement.scrollTop;

    state.isOpen = true;
    elements.slider?.classList.add(CONFIG.classes.open);
    elements.overlay?.classList.add(CONFIG.classes.active);

    // Use class to handle body scroll locking via CSS
    // Apply inline top offset to prevent visual jump when body becomes position:fixed
    document.body.style.top = `-${savedScrollPosition}px`;
    document.body.classList.add('cart-slider-open');

    // Focus trap
    elements.slider?.setAttribute('aria-hidden', 'false');

    // Hide badge when opening
    if (elements.tierBadge) elements.tierBadge.style.display = 'none';

    // Refresh cart data unless skipped
    if (!skipFetch) {
      fetchCart();
    }
  }

  function close() {
    state.isOpen = false;
    elements.slider?.classList.remove(CONFIG.classes.open);
    elements.overlay?.classList.remove(CONFIG.classes.active);

    // Remove the class first, then clear all inline styles
    document.body.classList.remove('cart-slider-open');

    // CRITICAL: Clear ALL body styles that might have been set
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.touchAction = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.style.height = '';

    // CRITICAL: Also clear any scroll-lock library attributes/styles it may have set
    document.body.removeAttribute('data-scroll-lock-locked');
    document.body.removeAttribute('data-scroll-lock-saved-overflow-y-property');
    document.body.removeAttribute('data-scroll-lock-saved-inline-overflow-property');
    document.body.removeAttribute('data-scroll-lock-saved-inline-overflow-y-property');
    document.body.removeAttribute('data-scroll-lock-filled-gap');
    document.body.removeAttribute('data-scroll-lock-current-fill-gap-method');
    document.documentElement.removeAttribute('data-scroll-locked');

    // Call theme's scroll-lock library to fully unlock if available
    if (window.themeVendor?.ScrollLock) {
      try {
        window.themeVendor.ScrollLock.clearQueueScrollLocks();
        window.themeVendor.ScrollLock.enablePageScroll();
      } catch (e) {
        console.warn('[CartSlider] Could not call theme scroll-lock:', e);
      }
    }

    // Restore scroll position after unlocking body
    window.scrollTo(0, savedScrollPosition);

    elements.slider?.setAttribute('aria-hidden', 'true');

    // Show badge on close if rewards are unlocked
    if (elements.tierBadge && state.unlockedTiers && state.unlockedTiers.size > 0) {
      elements.tierBadge.style.display = 'flex';
    }
  }

  // ============================================
  // PREVENT CHECKOUT REDIRECT
  // Prevent form from redirecting to checkout page
  // But DON'T stop propagation - let bundle apps handle via AJAX
  // ============================================
  // ============================================
  // COMPREHENSIVE CART MONITORING & INTERCEPTION
  // ============================================

  /* 
   * Debounce cart refresh to prevent multiple rapid calls 
   * increased to 300ms to handle bundle apps better 
   */
  let cartRefreshTimeout = null;
  const debouncedCartRefresh = (shouldOpen = false) => {
    // If add-to-cart triggered, open drawer immediately
    // open() calls fetchCart() internally, so no need for secondary refresh
    if (shouldOpen && !state.isOpen) {
      if (cartRefreshTimeout) clearTimeout(cartRefreshTimeout);
      cartRefreshTimeout = null;
      open();
      return;
    }

    if (cartRefreshTimeout) clearTimeout(cartRefreshTimeout);
    cartRefreshTimeout = setTimeout(() => {
      cartRefreshTimeout = null;
      fetchCart();
    }, 100);
  };

  /**
   * NON-BLOCKING ADD TO CART INTERCEPTION
   * Like GoKwik/Shopflow: Don't block native form submission
   * Let theme/bundle apps handle the actual add-to-cart via their own AJAX
   * Our fetch/XHR monitors (below) will detect cart changes and sync + open drawer
   */
  function interceptAddToCart() {
    // Only provide visual feedback - don't block native events
    // The fetch/XHR monitors below will detect /cart/add calls and trigger refresh
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('button[name="add"], input[name="add"], .product-form__submit, [data-add-to-cart]');
      if (!btn) return;
      if (btn.closest('[data-cart-slider]')) return;
      if (btn.name === 'checkout' || btn.getAttribute('data-action') === 'buy-now') return;

      // Just add loading state for visual feedback - don't block the event
      btn.classList.add('is-loading');
      setTimeout(() => btn.classList.remove('is-loading'), 2500);


    });

    // Note: We no longer block form submissions or button clicks
    // The existing fetch() and XMLHttpRequest monitors (lines 504-543) will:
    // 1. Detect when /cart/add is called by theme or bundle apps
    // 2. Trigger debouncedCartRefresh(true) which syncs cart and opens drawer
  }

  // ============================================
  // GLOBAL FETCH/XHR MONITORS
  // ============================================

  // URLs to monitor for cart changes
  const CART_ENDPOINTS = ['/cart/add', '/cart/change', '/cart/update', '/cart/clear'];

  const isCartEndpoint = (url) => {
    if (!url) return false;
    const urlStr = typeof url === 'string' ? url : url.toString();
    return CART_ENDPOINTS.some(endpoint => urlStr.includes(endpoint));
  };

  // 1. Monitor fetch() API
  const originalFetch = window.fetch;
  window.fetch = async function (...args) {
    const [url] = args;
    if (isCartEndpoint(url)) {
      if (state.internalUpdateCount > 0) return originalFetch.apply(this, args);

      const response = await originalFetch.apply(this, args);
      if (response.ok) {
        const isAddToCart = typeof url === 'string' && url.includes('/cart/add');
        debouncedCartRefresh(isAddToCart);
      }
      return response;
    }
    return originalFetch.apply(this, args);
  };

  // 2. Monitor XMLHttpRequest
  const originalXHROpen = XMLHttpRequest.prototype.open;
  const originalXHRSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    this._cartSliderUrl = url;
    this._cartSliderMethod = method;
    return originalXHROpen.apply(this, [method, url, ...rest]);
  };

  XMLHttpRequest.prototype.send = function (body) {
    const url = this._cartSliderUrl;
    if (isCartEndpoint(url)) {
      this.addEventListener('load', () => {
        if (state.internalUpdateCount > 0) return;
        if (this.status >= 200 && this.status < 300) {
          const isAddToCart = url && url.includes('/cart/add');
          debouncedCartRefresh(isAddToCart);
        }
      });
    }
    return originalXHRSend.apply(this, [body]);
  };

  // 3. Listen for Shopify's cart:refresh
  document.addEventListener('cart:refresh', () => {
    debouncedCartRefresh(false);
  });

  // 4. Listen for storage events
  window.addEventListener('storage', (e) => {
    if (e.key && (e.key.includes('cart') || e.key.includes('Cart'))) {
      debouncedCartRefresh(false);
    }
  });

  function interceptCartLinks() {
    // Comprehensive list of cart icon selectors used across different Shopify themes
    const CART_SELECTORS = [
      // Standard cart links

      'a[href="/cart"]',
      'a[href*="/cart"]:not([href*="/cart/"])',

      // Common cart icon classes (Dawn, Debut, Brooklyn, etc.)
      '.cart-icon',
      '.cart-icon-bubble',
      '.site-header__cart',
      '.header__cart',
      '.header-cart',
      '.header__icon--cart',
      '.js-drawer-open-cart',
      '.js-cart-drawer-trigger',
      '.cart-drawer-trigger',
      '.cart-toggle',
      '.cart-link',
      '.cart-button',
      '.CartToggle',
      '.header-cart-btn',
      '.mini-cart-trigger',
      '.minicart-trigger',
      '.cart-trigger',
      '.open-cart',
      '.js-open-cart',
      '.header__icon-wrapper--cart',

      // Data attributes
      '[data-cart-trigger]',
      '[data-cart-toggle]',
      '[data-cart-open]',
      '[data-open-cart]',
      '[data-drawer-toggle="cart"]',
      '[data-action="toggle-cart"]',
      '[data-action="open-cart"]',
      '[data-cart-drawer-trigger]',
      '[data-mini-cart-trigger]',
      '[data-cart-icon]',

      // Aria labels
      '[aria-label*="cart" i]',
      '[aria-label*="Cart" i]',
      '[aria-label*="Shopping bag" i]',
      '[aria-label*="shopping bag" i]',
      '[aria-label*="View cart" i]',

      // ID-based selectors
      '#cart-icon',
      '#cart-toggle',
      '#CartToggle',
      '#mini-cart-trigger',
      '#header-cart',

      // Button/icon combinations
      'button.cart',
      'button[class*="cart"]',
      '.icon-cart',
      '.icon-bag',

      // Theme specific
      '.header__icon--cart',
      'cart-icon-bubble',
      '.site-header__cart-toggle',
      '.cart-page-link',
      '.header-cart-btn',
      '.header-bar__cart-icon',

      // Generic patterns
      '[class*="cart-icon"]',
      '[class*="cart-trigger"]',
      '[class*="cart-toggle"]',
      '[class*="CartIcon"]',
      '[class*="minicart"]',
      '[class*="mini-cart"]'
    ];

    const cartSelectorString = CART_SELECTORS.join(', ');

    // Helper to handle the cart click
    const handleCartTrigger = (e) => {
      // 1. CRITICAL: Immediate exit if inside our cart slider (Fixes "stuck" scroll issues)
      if (e.target.closest('[data-cart-slider]')) return;

      // Explicitly ignore checkout button
      if (e.target.closest('[data-checkout-btn]')) return;

      // Ignore clicks inside the sticky add-to-cart bar (e.g. close button)
      if (e.target.closest('#cart-bar')) return;

      // Ignore actual add-to-cart button clicks to prevent them from being intercepted as cart links
      if (
        e.target.closest('[data-fbt-add-to-cart]') ||
        e.target.closest('[data-upsell-add]') ||
        e.target.closest('button[name="add"]') ||
        e.target.closest('input[name="add"]') ||
        e.target.closest('.product-form__submit') ||
        e.target.closest('[data-add-to-cart]')
      ) {
        return;
      }

      // 1. Check for standard cart links first
      const link = e.target.closest('a[href="/cart"], a[href*="/cart"]');

      // If it's a link to checkout or a sub-page of cart, ignore
      if (link && (link.href.includes('/checkout') || link.href.includes('/cart/') || link.href.endsWith('/cart') === false)) {
        // Special check: strict /cart end
        if (!link.href.endsWith('/cart') && !link.href.includes('/cart?')) {
          // Let it pass (e.g. /cart/change)
        } else {
          // It is a cart link
        }
      }

      let target = null;
      let shouldIntercept = false;

      // Check 1: Explicit Links
      if (link) {
        // Double check it's not a checkout link
        if (!link.href.includes('/checkout') && !link.href.includes('/cart/')) {
          target = link;
          shouldIntercept = true;
        }
      }

      // Check 2: Selectors
      if (!shouldIntercept) {
        const foundEl = e.target.closest(cartSelectorString);
        if (foundEl) {
          // Exclude our own slider - Robust checks
          if (foundEl.closest('[data-cart-slider]')) return;
          if (foundEl.closest('.cart-slider')) return;
          if (foundEl.closest('.cart-drawer')) return;

          // Exclude quantity inputs and buttons explicitly
          if (foundEl.closest('.cart-item__quantity-wrapper')) return;
          if (foundEl.closest('[data-qty-input]')) return;
          if (foundEl.matches('input[name="updates[]"]')) return;

          // Check if it's a link that we should actually allow (like checkout)
          if (foundEl.tagName === 'A') {
            const href = foundEl.getAttribute('href');
            if (href && (href.includes('/checkout') || href.includes('/cart'))) {
              // Allow navigation to cart page if it's explicitly a link
              // But if we want to intercept /cart links to open drawer:
              if (href.endsWith('/cart') || href.endsWith('/cart/') || href.includes('/cart?')) {
                // Intercept
              } else {
                return;
              }
            }
          }

          target = foundEl;
          shouldIntercept = true;
        }
      }

      // Check 3: SVG Icons (last resort)
      if (!shouldIntercept) {
        const clickedSvg = e.target.closest('svg');
        if (clickedSvg) {
          const parent = clickedSvg.parentElement;
          if (parent && (parent.tagName === 'A' || parent.tagName === 'BUTTON' || parent.getAttribute('role') === 'button')) {
            // Check contents of SVG
            const html = clickedSvg.outerHTML.toLowerCase();
            if (html.includes('cart') || html.includes('bag')) {
              // Exclude if inside our slider
              if (clickedSvg.closest('[data-cart-slider]')) return;

              target = parent;
              shouldIntercept = true;
            }
          }
        }
      }

      if (shouldIntercept && target) {
        // Prevent default navigation
        e.preventDefault();

        // STOP propagation to prevent theme cart from opening
        // We removed stopImmediatePropagation to allow other document listeners to run if needed
        e.stopPropagation();

        open();
        return false;
      }
    };

    // Use capturing phase (true) to catch event before theme
    // FIXED: Only use 'click' - touchstart with passive:false was blocking mobile scroll
    document.addEventListener('click', handleCartTrigger, { capture: true });

    // Also listen for theme-specific cart events and override them
    // Also listen for theme-specific cart events and override them
    document.addEventListener('cart:open', (e) => {
      e.preventDefault();
      e.stopPropagation();
      open();
    }, true);

    document.addEventListener('theme:cart:toggle', (e) => {
      e.preventDefault();
      e.stopPropagation();
      open();
    }, true);

    // Hide any existing theme cart drawers when our slider opens
    const style = document.createElement('style');
    style.textContent = `
      /* Hide theme cart drawers when our slider is open */
      body.cart-slider-open .cart-drawer,
      body.cart-slider-open .mini-cart,
      body.cart-slider-open .drawer--cart,
      body.cart-slider-open [data-cart-drawer],
      body.cart-slider-open .ajax-cart,
      body.cart-slider-open .site-nav__cart,
      body.cart-slider-open .cart-notification,
      body.cart-slider-open #CartDrawer,
      body.cart-slider-open [id^="shopify-section-header"] .cart-drawer {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
        z-index: -1 !important;
      }
    `;
    document.head.appendChild(style);
  }

  // ============================================
  // CART API FUNCTIONS
  // ============================================
  async function fetchCart() {
    // If already fetching, mark that we need another update immediately after
    if (state.isFetching) {
      state.needsRefetch = true;
      return;
    }

    state.isFetching = true;
    state.internalUpdateCount++; // Increment counter to prevent loops

    try {

      // Cache busting to ensure fresh data (fixes upsell unlock delays)
      const res = await fetch(`/cart.js?v=${Date.now()}`, {
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!res.ok) {
        throw new Error(`Cart fetch failed: ${res.status}`);
      }

      const cart = await res.json();
      state.cart = cart;

      updateCartUI();
      updateTierProgress();
      checkTierRewards();
      dispatchCartEvent(); // Notify 3rd party apps
    } catch (err) {
      console.error('[CartSlider] Cart fetch failed:', err);
    } finally {
      state.isFetching = false;
      state.internalUpdateCount = Math.max(0, state.internalUpdateCount - 1);

      // If a request came in while we were fetching, trigger one more fetch
      if (state.needsRefetch) {
        state.needsRefetch = false;
        fetchCart();
      }
    }
  }

  async function addToCart(data, properties = {}, options = {}) {
    // If we are already loading, wait or ignore to prevent race conditions
    if (state.isLoading && !options.force) return false;

    // Track if WE set loading, so we only clear it if we set it
    const weSetLoading = !options.force && !state.isLoading;
    if (weSetLoading) setLoading(true);

    try {
      let body;
      let headers = {};

      if (data instanceof FormData) {
        // Check if this is a bundle/pack with multiple variant IDs (flavor selectors)
        const allIds = data.getAll('id');



        if (allIds.length > 1) {
          // Multiple variant IDs detected - this is a bundle with multiple flavor selections
          // We need to add each item separately using the items[] format

          // Count occurrences of each variant ID to determine quantities
          const variantQuantities = {};
          allIds.forEach(id => {
            const variantId = String(id);
            variantQuantities[variantId] = (variantQuantities[variantId] || 0) + 1;
          });



          // Build items array for Shopify API
          const items = Object.entries(variantQuantities).map(([id, quantity]) => ({
            id: parseInt(id, 10),
            quantity: quantity,
            properties: properties
          }));

          body = JSON.stringify({ items });
          headers = { 'Content-Type': 'application/json' };

        } else {
          // Single variant - use FormData as before
          for (const [key, value] of Object.entries(properties)) {
            data.append(`properties[${key}]`, value);
          }
          body = data;
        }
      } else {
        // Object format - wrap in items array for 100% reliable Shopify JSON API compatibility
        body = JSON.stringify({
          items: [{
            id: parseInt(data.id, 10),
            quantity: parseInt(data.quantity || 1, 10),
            properties: { ...data.properties, ...properties }
          }]
        });
        headers = { 'Content-Type': 'application/json' };
      }

      state.internalUpdateCount++; // Increment counter before fetch

      const response = await fetch(CONFIG.api.add, {
        method: 'POST',
        body: body,
        headers: headers
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to add item');
      }

      await fetchCart();
      updateTierProgress();
      checkTierRewards();

      return true;
    } catch (error) {
      console.error('Add to cart error:', error);
      showNotification(error.message, 'error');
      return false;
    } finally {
      state.internalUpdateCount = Math.max(0, state.internalUpdateCount - 1); // Decrement counter
      // Only clear loading if we set it
      if (weSetLoading) setLoading(false);
    }
  }

  async function updateItemQuantity(key, quantity) {
    setLoading(true); // Show loader during update
    state.internalUpdateCount++;
    const requestedQuantity = quantity;

    // Get cart item element for optimistic update
    const cartItemEl = document.querySelector(`[data-line-key="${key}"]`);
    const variantId = cartItemEl ? Number(cartItemEl.dataset.variantId) : null;

    // OPTIMISTIC UI: Update input immediately for instant feedback
    const input = cartItemEl?.querySelector('[data-qty-input]');
    if (input && input !== document.activeElement) {
      input.value = quantity;
    }

    try {
      // FAST: Removed inventory pre-fetch - Shopify handles this automatically
      // Make the cart update request directly
      const response = await fetch(CONFIG.api.change, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });

      if (!response.ok) throw new Error('Failed to update quantity');

      const updatedCart = await response.json();

      // FAST: Use response directly instead of calling fetchCart() again
      state.cart = updatedCart;

      // Find the updated item to check if Shopify adjusted quantity
      const updatedItem = variantId
        ? updatedCart.items.find(item => item.variant_id === variantId)
        : updatedCart.items.find(item => item.key === key);

      // Show notification only if Shopify adjusted quantity (inventory limit)
      if (updatedItem && requestedQuantity > updatedItem.quantity) {
        showNotification(`Only ${updatedItem.quantity} available in stock.`, 'warning');
        // Update input to actual quantity
        if (input) input.value = updatedItem.quantity;
      } else if (!updatedItem && requestedQuantity > 0) {
        showNotification('Sorry, this item is now out of stock', 'error');
      }

      // Update UI with the cart data we already have
      updateCartUI();
      updateTierProgress();
      checkTierRewards();
      dispatchCartEvent();

      return true;
    } catch (error) {
      console.error('Update quantity error:', error);
      showNotification('Failed to update quantity', 'error');
      // Revert optimistic update by refreshing cart
      await fetchCart();
      return false;
    } finally {
      state.internalUpdateCount = Math.max(0, state.internalUpdateCount - 1);
      setLoading(false); // Hide loader when done
    }
  }

  async function removeItem(key) {
    return updateItemQuantity(key, 0);
  }

  // ============================================
  // UI UPDATE FUNCTIONS
  // ============================================
  async function updateCartUI() {
    if (!state.cart) return;

    // Update cart count badges
    document.querySelectorAll('[data-cart-count], .cart-count, .cart-count-bubble').forEach(el => {
      el.textContent = state.cart.item_count;
    });

    // Disable/Enable checkout button based on cart contents
    const checkoutBtn = document.querySelector('[data-checkout-btn]');
    if (checkoutBtn) {
      if (state.cart.item_count === 0) {
        checkoutBtn.disabled = true;
        checkoutBtn.classList.add('disabled');
        checkoutBtn.style.opacity = '0.6';
        checkoutBtn.style.cursor = 'not-allowed';
        checkoutBtn.style.background = '#9ca3af'; // Gray background when disabled
        checkoutBtn.style.pointerEvents = 'none'; // Prevent clicks entirely
      } else {
        checkoutBtn.disabled = false;
        checkoutBtn.classList.remove('disabled');
        checkoutBtn.style.opacity = '1';
        checkoutBtn.style.cursor = 'pointer';
        checkoutBtn.style.background = ''; // Reset to default (uses inline style from Liquid)
        checkoutBtn.style.pointerEvents = 'auto';
      }
    }

    // NON-BLOCKING: Fetch variant data in background to get accurate compare_at_price
    // We do NOT await this anymore, so the UI is snappy
    const bgFetch = fetchAllVariantData().then(() => {
      // Once data is back, update prices only if needed
      updatePricesAfterFetch();
    });

    // Calculate Totals & Savings using CURRENTLY cached data
    let totalOriginalPrice = 0;
    state.cart.items.forEach(item => {
      // Exclude reward items from totals calculation
      if (item.properties?._reward_tier || item.properties?._is_free_gift) return;

      // Get cached variant data for accurate compare_at_price
      const cacheKey = `${item.product_id}_${item.variant_id}`;
      const variantData = state.variantDataCache[cacheKey];

      let itemOriginalPrice = item.original_price;

      // Use compare_at_price from Product API if available and higher
      if (variantData && variantData.compare_at_price) {
        const compareAtPrice = Math.round(parseFloat(variantData.compare_at_price) * 100);
        if (compareAtPrice > item.final_price) {
          itemOriginalPrice = compareAtPrice;
        }
      }

      totalOriginalPrice += itemOriginalPrice * item.quantity;
    });

    // Use effective total (excluding rewards) for UI
    const totalPrice = getEffectiveCartTotal();
    const totalSavings = totalOriginalPrice - totalPrice;
    const savingsPercent = totalOriginalPrice > 0 ? Math.round((totalSavings / totalOriginalPrice) * 100) : 0;

    // Update Footer Elements
    const subtotalEl = document.querySelector(CONFIG.selectors.cartSubtotal);
    const originalTotalEl = document.querySelector('[data-cart-original-total]');
    const savingsPercentEl = document.querySelector('[data-cart-savings-percent]');
    const savingsBanner = document.querySelector('[data-savings-banner]');
    const savingsText = document.querySelector('[data-savings-text]');

    // Calculate tier discount if unlocked
    // Calculate tier discount if unlocked
    const tierDiscount = getUnlockedTierDiscount();
    // tierDiscount.amount is already calculated in cents in the helper function
    const tierDiscountAmount = tierDiscount.amount;
    const estimatedFinalPrice = totalPrice - tierDiscountAmount;

    // Calculate TOTAL savings (product discounts + tier discount)
    const totalCombinedSavings = totalSavings + tierDiscountAmount;
    // Calculate combined savings percentage from original total to final estimated price
    const combinedSavingsPercent = totalOriginalPrice > 0 ? Math.round((totalCombinedSavings / totalOriginalPrice) * 100) : 0;

    // 1. Final Price - Show estimated discounted price if tier discount is unlocked
    if (subtotalEl) {
      if (tierDiscountAmount > 0) {
        subtotalEl.textContent = formatMoney(estimatedFinalPrice);
      } else {
        subtotalEl.textContent = formatMoney(totalPrice);
      }
    }

    // 2. Original Price - Always show original total if there are ANY savings
    if (originalTotalEl) {
      if (totalCombinedSavings > 0) {
        // Always show the TRUE original price (before any discounts)
        originalTotalEl.textContent = formatMoney(totalOriginalPrice);
        originalTotalEl.style.display = 'inline';
      } else {
        originalTotalEl.style.display = 'none';
      }
    }

    // 3. Percentage Tag - Show COMBINED total discount percentage
    if (savingsPercentEl) {
      if (combinedSavingsPercent > 0) {
        savingsPercentEl.textContent = `(${combinedSavingsPercent}% OFF)`;
        savingsPercentEl.style.display = 'inline';
      } else {
        savingsPercentEl.style.display = 'none';
      }
    }

    // 4. Savings Banner - Include tier discount savings
    if (savingsBanner && savingsText) {
      const totalSavingsWithTier = totalSavings + tierDiscountAmount;
      if (totalSavingsWithTier > 0) {
        savingsBanner.style.display = 'block';
        savingsText.textContent = `${formatMoney(totalSavingsWithTier)} Saved so far!`;
      } else {
        savingsBanner.style.display = 'none';
      }
    }

    // 5. Tier Discount Banner
    updateTierDiscountBanner(tierDiscount, tierDiscountAmount);

    // 6. Price breakdown panel — refresh content with latest data
    updatePriceBreakdown({
      totalOriginalPrice,
      totalPrice,
      totalSavings,
      tierDiscount,
      tierDiscountAmount,
      estimatedFinalPrice
    });

    // Update items list (using currently cached data)
    renderCartItems();

    // Update discounted offers visibility
    updateDiscountedOffers();

    // NEW: Validate checkout rules (block checkout if needed)
    validateCheckoutRules();
  }


  // Get the highest unlocked tier discount (percentage or fixed amount)
  function getUnlockedTierDiscount() {
    if (!state.cart || !state.config?.tiers?.length) {
      return { type: 'none', value: 0, amount: 0, label: '' };
    }

    // Use effective cart total (excluding rewards) - consistent with tier progress system
    const cartTotal = getEffectiveCartTotal();
    let bestDiscount = { type: 'none', value: 0, amount: 0, label: '' };



    state.config.tiers.forEach((tier, idx) => {
      const isUnlocked = cartTotal >= tier.threshold;



      if (isUnlocked) {
        let currentAmount = 0;
        let currentType = 'none';
        let currentValue = 0;

        if (tier.offerType === 'percentage_discount') {
          currentType = 'percentage';
          currentValue = tier.discountPercent;
          currentAmount = Math.round(cartTotal * (tier.discountPercent / 100));
        } else if (tier.offerType === 'fixed_amount_off') {
          currentType = 'fixed';
          currentValue = tier.discountAmount || 0;
          currentAmount = tier.discountAmount || 0;
        }



        // Compare amounts to find the single best offer
        if (currentAmount > bestDiscount.amount) {
          bestDiscount = {
            type: currentType,
            value: currentValue,
            amount: currentAmount,
            label: tier.label || (currentType === 'percentage' ? `${currentValue}% Off` : `Discount`)
          };
        }
      }
    });


    return bestDiscount;
  }

  // ============================================
  // PRICE BREAKDOWN PANEL
  // ============================================

  let _breakdownData = null; // latest computed data

  /**
   * Store the latest computed totals so we can re-render the panel on open.
   */
  function updatePriceBreakdown(data) {
    _breakdownData = data;
    // If the panel is currently open, refresh its content immediately
    const panel = document.querySelector('[data-price-breakdown]');
    if (panel && panel.style.display !== 'none') {
      renderPriceBreakdown(panel, data);
    }
  }

  function togglePriceBreakdown() {
    // Respect the theme setting — do nothing if feature is disabled
    if (!state.config?.enablePriceBreakdown) return;

    const panel = document.querySelector('[data-price-breakdown]');
    if (!panel) return;
    if (panel.style.display === 'none' || panel.style.display === '') {
      openPriceBreakdown(panel);
    } else {
      closePriceBreakdown();
    }
  }

  function openPriceBreakdown(panel) {
    if (!panel) panel = document.querySelector('[data-price-breakdown]');
    if (!panel) return;
    if (_breakdownData) renderPriceBreakdown(panel, _breakdownData);
    panel.style.display = 'block';
    // Slight animation
    panel.style.opacity = '0';
    panel.style.transform = 'translateY(6px)';
    panel.style.transition = 'opacity 0.2s ease, transform 0.2s ease';
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        panel.style.opacity = '1';
        panel.style.transform = 'translateY(0)';
      });
    });
    // Rotate chevron up
    const chevron = document.querySelector('[data-breakdown-chevron]');
    if (chevron) chevron.style.transform = 'rotate(180deg)';
  }

  function closePriceBreakdown() {
    const panel = document.querySelector('[data-price-breakdown]');
    if (!panel) return;
    panel.style.opacity = '0';
    panel.style.transform = 'translateY(6px)';
    panel.style.transition = 'opacity 0.2s ease, transform 0.2s ease';
    setTimeout(() => { panel.style.display = 'none'; }, 200);
    // Rotate chevron back down
    const chevron = document.querySelector('[data-breakdown-chevron]');
    if (chevron) chevron.style.transform = 'rotate(0deg)';
  }

  /**
   * Build the HTML rows for the price breakdown panel.
   */
  function renderPriceBreakdown(panel, data) {
    const {
      totalOriginalPrice,
      totalPrice,
      totalSavings,
      tierDiscount,
      tierDiscountAmount,
      estimatedFinalPrice
    } = data;

    const rowsEl = panel.querySelector('[data-price-breakdown-rows]');
    const totalEl = panel.querySelector('[data-price-breakdown-total]');
    if (!rowsEl || !totalEl) return;

    const rows = [];

    // --- Row 1: Cart Subtotal (original / compare_at prices) ---
    rows.push(`
      <div class="cpb-row">
        <span class="cpb-row__label">Cart Subtotal</span>
        <span class="cpb-row__value">${formatMoney(totalOriginalPrice)}</span>
      </div>
    `);

    // ============================================================
    // --- ACTUAL SHOPIFY DISCOUNTS (named, from cart API) ---
    // Shopify puts applied discounts in two places:
    //   1. item.discounts[]              -> per line item allocations
    //   2. cart.discount_applications[]  -> cart-level (codes, automatic, script)
    // We aggregate both, grouped by title, showing each separately.
    // ============================================================

    // 1. Collect line-item discounts (grouped by title)
    const namedDiscounts = {}; // title -> total cents saved across cart

    if (state.cart?.items) {
      state.cart.items.forEach(item => {
        // Skip reward/free-gift items
        if (item.properties?._reward_tier || item.properties?._is_free_gift) return;

        // item.discounts: [{title, amount}] — amounts in store currency cents
        if (Array.isArray(item.discounts)) {
          item.discounts.forEach(d => {
            const key = (d.title || 'Discount').trim();
            const amt = parseInt(d.amount, 10) || 0;
            namedDiscounts[key] = (namedDiscounts[key] || 0) + amt;
          });
        }
      });
    }

    // 2. Cart-level discount_applications (discount codes, automatic discounts)
    // These are returned by the Storefront API — not always in cart.js, but some themes expose them.
    if (Array.isArray(state.cart?.discount_applications)) {
      state.cart.discount_applications.forEach(app => {
        const key = (app.title || app.code || app.type || 'Discount').trim();
        const amt = parseInt(app.total_allocated_amount, 10) || 0;
        if (!namedDiscounts[key] && amt > 0) {
          namedDiscounts[key] = amt;
        }
      });
    }

    // 3. Fallback: cart.discounts (simpler object some older APIs return)
    if (Array.isArray(state.cart?.discounts)) {
      state.cart.discounts.forEach(d => {
        const key = (d.title || d.code || 'Discount').trim();
        let amt = parseFloat(d.amount) || 0;
        // If the value looks like dollars not cents, convert
        if (amt > 0 && amt < 1000 && state.cart.total_price > 100) {
          amt = Math.round(amt * 100);
        }
        if (!namedDiscounts[key] && amt > 0) {
          namedDiscounts[key] = Math.round(amt);
        }
      });
    }

    // Total cents accounted for by named Shopify discounts
    const namedDiscountTotal = Object.values(namedDiscounts).reduce((s, v) => s + v, 0);

    // 4. Render each named discount row
    Object.entries(namedDiscounts).forEach(([title, amount]) => {
      if (amount <= 0) return;
      // Heuristic: likely a coupon code if it's uppercase with no spaces
      const isCode = /^[A-Z0-9_\-]{3,}$/.test(title);
      const icon = isCode ? '\uD83C\uDF9F\uFE0F' : '\uD83C\uDFF7\uFE0F';
      const sourceLabel = isCode ? 'Discount code applied' : 'Automatic discount';

      rows.push(`
        <div class="cpb-row cpb-row--saving">
          <span class="cpb-row__label">
            <span class="cpb-row__icon">${icon}</span>${escapeHtml(title)}
            <span class="cpb-row__source">${sourceLabel}</span>
          </span>
          <span class="cpb-row__value cpb-row__value--green">\u2212 ${formatMoney(amount)}</span>
        </div>
      `);
    });

    // 5. Residual compare_at_price savings (products on sale, no explicit discount name)
    const residualSavings = totalSavings - namedDiscountTotal;
    if (residualSavings > 50) { // threshold avoids floating-point noise
      rows.push(`
        <div class="cpb-row cpb-row--saving">
          <span class="cpb-row__label">
            <span class="cpb-row__icon">\uD83D\uDCB0</span>Product Sale Prices
            <span class="cpb-row__source">Items discounted from original price</span>
          </span>
          <span class="cpb-row__value cpb-row__value--green">\u2212 ${formatMoney(residualSavings)}</span>
        </div>
      `);
    }

    // ============================================================
    // --- TIER DISCOUNTS (our custom config — applied at checkout) ---
    // ============================================================
    if (tierDiscountAmount > 0 && state.config?.tiers?.length) {
      const unlockedTiers = state.config.tiers.filter(tier => {
        if (tier.offerType === 'percentage_discount' || tier.offerType === 'fixed_amount_off') {
          return isTierUnlocked(tier, state.cart);
        }
        return false;
      });

      if (tierDiscount.type !== 'none') {
        let tierLabel = tierDiscount.label || '';
        let tierSource = '';
        const matchingTier = unlockedTiers.find(t => {
          if (tierDiscount.type === 'percentage') return t.offerType === 'percentage_discount' && t.discountPercent === tierDiscount.value;
          if (tierDiscount.type === 'fixed') return t.offerType === 'fixed_amount_off';
          return false;
        });
        if (matchingTier) {
          tierSource = `Spend \u20b9${(matchingTier.threshold / 100).toFixed(0)} offer`;
          if (!tierLabel) {
            tierLabel = tierDiscount.type === 'percentage' ? `${tierDiscount.value}% Off` : `Flat Discount`;
          }
        }

        let discountDesc = '';
        if (tierDiscount.type === 'percentage') {
          discountDesc = `${tierDiscount.value}% off your order`;
        } else if (tierDiscount.type === 'fixed') {
          discountDesc = `Flat ${formatMoney(tierDiscountAmount)} off`;
        }

        rows.push(`
          <div class="cpb-row cpb-row--saving cpb-row--tier">
            <span class="cpb-row__label">
              <span class="cpb-row__icon">\uD83C\uDF89</span>${escapeHtml(tierLabel)}
              ${tierSource ? `<span class="cpb-row__source">${escapeHtml(tierSource)}${discountDesc ? ' \u00b7 ' + escapeHtml(discountDesc) : ''}</span>` : ''}
            </span>
            <span class="cpb-row__value cpb-row__value--green">\u2212 ${formatMoney(tierDiscountAmount)}</span>
          </div>
        `);
      }
    }

    // ============================================================
    // --- NON-MONETARY TIER REWARDS (informational badges) ---
    // ============================================================
    if (state.config?.tiers?.length) {
      const nonMonetaryUnlocked = state.config.tiers.filter(tier => {
        const isMonetary = tier.offerType === 'percentage_discount' || tier.offerType === 'fixed_amount_off';
        return !isMonetary && isTierUnlocked(tier, state.cart);
      });
      nonMonetaryUnlocked.forEach(tier => {
        let offerDesc = '';
        if (tier.offerType === 'free_product') offerDesc = 'Free gift unlocked';
        else if (tier.offerType === 'free_shipping') offerDesc = 'Free shipping unlocked';
        else if (tier.offerType === 'discounted_product') offerDesc = 'Special priced product';
        else if (tier.offerType === 'buy_x_get_y') offerDesc = 'Buy X Get Y active';
        else offerDesc = tier.label || 'Reward unlocked';

        rows.push(`
          <div class="cpb-row cpb-row--reward">
            <span class="cpb-row__label">
              <span class="cpb-row__icon">\uD83C\uDF81</span>${escapeHtml(tier.label || offerDesc)}
              <span class="cpb-row__source">Spend \u20b9${(tier.threshold / 100).toFixed(0)} offer \u00b7 ${escapeHtml(offerDesc)}</span>
            </span>
            <span class="cpb-row__value cpb-row__value--badge">UNLOCKED</span>
          </div>
        `);
      });
    }

    // If no discount rows at all, show a placeholder
    if (rows.length === 1) {
      rows.push(`
        <div class="cpb-row">
          <span class="cpb-row__label" style="color:#aaa;font-size:12px;">No discounts applied yet</span>
        </div>
      `);
    }

    rowsEl.innerHTML = rows.join('');

    // --- Total row ---
    const finalAmt = tierDiscountAmount > 0 ? estimatedFinalPrice : totalPrice;
    let totalHtml = `
      <div class="cpb-total__row">
        <span class="cpb-total__label">Estimated Total</span>
        <span class="cpb-total__value">${formatMoney(finalAmt)}</span>
      </div>
    `;
    if (tierDiscountAmount > 0) {
      totalHtml += `<p class="cpb-total__note">Tier discount applied at checkout</p>`;
    }
    totalEl.innerHTML = totalHtml;
  }

  // Track banner fade timeout to prevent multiple timeouts
  let bannerFadeTimeout = null;

  // Update the tier discount banner display
  function updateTierDiscountBanner(tierDiscount, discountAmount) {
    const discountBanner = document.querySelector('[data-tier-discount-banner]');
    const discountText = document.querySelector('[data-tier-discount-text]');
    const discountNote = document.querySelector('[data-tier-discount-note]');

    if (!discountBanner) return;

    // Clear any existing fade timeout
    if (bannerFadeTimeout) {
      clearTimeout(bannerFadeTimeout);
      bannerFadeTimeout = null;
    }

    if (discountAmount > 0) {
      // Reset opacity and show banner
      discountBanner.style.opacity = '1';
      discountBanner.style.display = 'block';
      discountBanner.style.transition = 'opacity 0.5s ease';

      if (discountText) {
        if (tierDiscount.type === 'percentage') {
          discountText.innerHTML = `🎉 <strong>${tierDiscount.value}% OFF</strong> unlocked! You save <strong>${formatMoney(discountAmount)}</strong>`;
        } else if (tierDiscount.type === 'fixed') {
          discountText.innerHTML = `🎉 <strong>${formatMoney(discountAmount)} OFF</strong> unlocked!`;
        }
      }
      if (discountNote) {
        discountNote.textContent = 'Discount applies at checkout';
      }
    } else {
      discountBanner.style.display = 'none';
    }
  }


  /**
   * Fetch variant data (including compare_at_price) from Product API
   * This is needed because the Cart API doesn't include compare_at_price
   */
  async function fetchVariantData(productId, variantId, productHandle) {
    // Check cache first
    const cacheKey = `${productId}_${variantId}`;
    if (state.variantDataCache[cacheKey]) {
      return state.variantDataCache[cacheKey];
    }

    try {
      // Use product handle for the API (Shopify requires handle, not numeric ID)
      const response = await fetch(`/products/${productHandle}.json`);
      if (!response.ok) return null;

      const productData = await response.json();
      const variant = productData.product.variants.find(v => v.id === variantId);

      if (variant) {
        // Cache all variants from this product for future use
        productData.product.variants.forEach(v => {
          const key = `${productId}_${v.id}`;
          state.variantDataCache[key] = {
            compare_at_price: v.compare_at_price,
            price: v.price,
            inventory_quantity: v.inventory_quantity,
            inventory_management: v.inventory_management
          };
        });

        return state.variantDataCache[cacheKey];
      }
    } catch (error) {
      console.warn('[CartSlider] Could not fetch variant data:', error);
    }

    return null;
  }

  /**
   * Extract product handle from cart item URL
   * URL format: /products/product-handle or /products/product-handle?variant=123
   */
  function extractProductHandle(url) {
    if (!url) return null;
    const match = url.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  /**
   * Fetch all variant data for cart items in parallel
   */

  /**
   * Fetch all variant data for cart items in parallel
   */
  async function fetchAllVariantData() {
    if (!state.cart || !state.cart.items) return;

    // Guard: skip if a variant fetch is already in progress
    if (state.isVariantFetchInProgress) return;
    state.isVariantFetchInProgress = true;

    try {
      // Collect unique products that need fetching (by handle)
      const productsToFetch = new Map(); // handle -> productId
      let needsUpdate = false;

      state.cart.items.forEach(item => {
        const cacheKey = `${item.product_id}_${item.variant_id}`;
        // Check both runtime cache and session storage again
        if (!state.variantDataCache[cacheKey]) {
          const handle = extractProductHandle(item.url);
          if (handle) {
            productsToFetch.set(handle, item.product_id);
          }
        }
      });

      // Fetch all products in parallel
      const fetchPromises = Array.from(productsToFetch.entries()).map(async ([handle, productId]) => {
        try {
          const response = await fetch(`/products/${handle}.json`);
          if (!response.ok) return;

          const productData = await response.json();

          // Cache all variants from this product
          productData.product.variants.forEach(v => {
            const key = `${productId}_${v.id}`;
            state.variantDataCache[key] = {
              compare_at_price: v.compare_at_price,
              price: v.price,
              inventory_quantity: v.inventory_quantity,
              inventory_management: v.inventory_management
            };
          });

          needsUpdate = true;
        } catch (error) {
          // Silent fail
        }
      });

      await Promise.all(fetchPromises);

      // Save to session storage if we updated anything
      if (needsUpdate) {
        try {
          sessionStorage.setItem('cartSliderVariantCache', JSON.stringify(state.variantDataCache));
        } catch (e) { }
      }
    } finally {
      state.isVariantFetchInProgress = false;
    }
  }

  // Optimized function to update ONLY prices after background fetch
  function updatePricesAfterFetch() {
    if (!state.cart) return;

    // Re-calculate totals with new data
    let totalOriginalPrice = 0;
    let hasChanges = false;

    // Update individual item prices in DOM
    state.cart.items.forEach(item => {
      if (item.properties?._reward_tier || item.properties?._is_free_gift) return;

      const cacheKey = `${item.product_id}_${item.variant_id}`;
      const variantData = state.variantDataCache[cacheKey];

      // Calculate true original price
      let itemOriginalPrice = item.original_price;
      if (variantData && variantData.compare_at_price) {
        const compareAtPrice = Math.round(parseFloat(variantData.compare_at_price) * 100);
        if (compareAtPrice > item.final_price) {
          itemOriginalPrice = compareAtPrice;
        }
      }

      totalOriginalPrice += itemOriginalPrice * item.quantity;

      // Update DOM for this item
      const itemEl = document.querySelector(`[data-line-key="${item.key}"]`);
      if (itemEl) {
        const priceEl = itemEl.querySelector('.cart-item__original-price');
        if (priceEl && itemOriginalPrice > item.final_price) {
          const newPriceText = formatMoney(itemOriginalPrice);
          if (priceEl.textContent !== newPriceText) {
            priceEl.textContent = newPriceText;
            // Also update badge if exists
            const badgeEl = itemEl.querySelector('.cart-item__discount-badge');
            if (badgeEl) {
              const discountPercent = Math.round((itemOriginalPrice - item.final_price) / itemOriginalPrice * 100);
              badgeEl.textContent = `(${discountPercent}% OFF)`;
            }
          }
        } else if (!priceEl && itemOriginalPrice > item.final_price) {
          // Price element was missing but now we have a discount -> Update in-place
          const variantData = state.variantDataCache[cacheKey];
          updateCartItemDOM(itemEl, item, variantData);
          hasChanges = true;
        }
      }
    });

    // Update Footer Totals regardless (no full re-render needed)
    if (hasChanges) {
      // Prices were updated in-place above, no need to re-render
    }

    // Update Footer Totals
    const totalPrice = getEffectiveCartTotal();
    const totalSavings = totalOriginalPrice - totalPrice;
    const combinedSavingsPercent = totalOriginalPrice > 0 ? Math.round((totalSavings / totalOriginalPrice) * 100) : 0;

    const originalTotalEl = document.querySelector('[data-cart-original-total]');
    const savingsPercentEl = document.querySelector('[data-cart-savings-percent]');

    if (originalTotalEl) {
      if (totalSavings > 0) {
        originalTotalEl.textContent = formatMoney(totalOriginalPrice);
        originalTotalEl.style.display = 'inline';
      } else {
        originalTotalEl.style.display = 'none';
      }
    }

    if (savingsPercentEl) {
      if (combinedSavingsPercent > 0) {
        savingsPercentEl.textContent = `(${combinedSavingsPercent}% OFF)`;
        savingsPercentEl.style.display = 'inline';
      } else {
        savingsPercentEl.style.display = 'none';
      }
    }
  }

  function renderCartItems() {
    // Try to re-cache cartItems element if it's missing
    if (!elements.cartItems) {
      elements.cartItems = document.querySelector(CONFIG.selectors.cartItems);
    }

    if (!elements.cartItems) {
      console.warn('[CartSlider] Cart items container not found');
      return;
    }

    if (!state.cart) {
      console.warn('[CartSlider] No cart state available');
      return;
    }

    if (state.cart.item_count === 0) {
      elements.cartItems.innerHTML = `
        <div class="cart-slider__empty" data-cart-empty>
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <circle cx="9" cy="21" r="1"/>
            <circle cx="20" cy="21" r="1"/>
            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
          </svg>
          <p>Your cart is empty</p>
          <a href="/collections/all" class="cart-slider__continue-btn">Continue Shopping</a>
        </div>
      `;
      return;
    }

    // --- CLEAN IN-PLACE DOM UPDATE (no DocumentFragment detaching) ---

    // 1. Map existing items by Line Key
    const existingItems = {};
    elements.cartItems.querySelectorAll('[data-cart-item]').forEach(el => {
      existingItems[el.dataset.lineKey] = el;
    });

    const activeKeys = new Set();

    // 2. Update or create items, ensure correct order
    state.cart.items.forEach((item, index) => {
      activeKeys.add(item.key);

      const cacheKey = `${item.product_id}_${item.variant_id}`;
      const variantData = state.variantDataCache[cacheKey] || null;

      let itemEl = existingItems[item.key];

      if (itemEl) {
        // Item exists -> Update in-place (no flicker)
        updateCartItemDOM(itemEl, item, variantData);
      } else {
        // Item is new -> Create it
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = renderCartItem(item, variantData);
        itemEl = tempDiv.firstElementChild;
      }

      // Ensure correct position without detaching from DOM
      const currentChild = elements.cartItems.children[index];
      if (currentChild !== itemEl) {
        elements.cartItems.insertBefore(itemEl, currentChild || null);
      }
    });

    // 3. Remove items no longer in cart
    while (elements.cartItems.children.length > state.cart.items.length) {
      elements.cartItems.lastElementChild.remove();
    }
  }

  // Helper to update existing item without destroying it
  function updateCartItemDOM(el, item, variantData) {
    // 1. Calculate Prices (logic copied from renderCartItem)
    let compareAtPrice = null;
    if (variantData && variantData.compare_at_price) {
      compareAtPrice = Math.round(parseFloat(variantData.compare_at_price) * 100);
    }
    let originalPrice = item.original_price;
    let hasDiscount = false;
    let discountPercent = 0;

    if (compareAtPrice && compareAtPrice > item.final_price) {
      originalPrice = compareAtPrice;
      hasDiscount = true;
      discountPercent = Math.round((compareAtPrice - item.final_price) / compareAtPrice * 100);
    } else if (item.original_price > item.final_price) {
      originalPrice = item.original_price;
      hasDiscount = true;
      discountPercent = Math.round((item.original_price - item.final_price) / item.original_price * 100);
    }

    const isReward = item.properties?._reward_tier || item.properties?._is_free_gift;
    if (isReward) {
      hasDiscount = true;
      originalPrice = item.original_price;
      if (compareAtPrice && compareAtPrice > item.original_price) originalPrice = compareAtPrice;
      discountPercent = 100;
    }

    // 2. Update Data Attributes
    if (el.dataset.variantId !== item.variant_id.toString()) el.dataset.variantId = item.variant_id;
    el.dataset.finalPrice = item.final_price;
    el.dataset.compareAtPrice = originalPrice;

    // 3. Update Elements

    // Quantity Input (Only if not focused and different)
    const qtyInput = el.querySelector('[data-qty-input]');
    if (qtyInput && qtyInput !== document.activeElement && qtyInput.value != item.quantity) {
      qtyInput.value = item.quantity;
    }

    // Quantity Lock: update minus button and input lock state
    const lockedQty = getLockedQty(item);
    const isLocked = lockedQty > 0 && item.quantity <= lockedQty;
    const minusBtn = el.querySelector('[data-qty-decrease]');
    if (minusBtn) {
      minusBtn.disabled = isLocked;
      minusBtn.classList.toggle('cart-item__qty-btn--locked', isLocked);
      minusBtn.title = isLocked ? 'Minimum quantity: ' + lockedQty : '';
    }
    if (qtyInput) {
      if (lockedQty > 0) qtyInput.min = lockedQty;
    }

    // Title & Variant
    const titleLink = el.querySelector('.cart-item__title');
    if (titleLink && titleLink.textContent !== item.product_title) titleLink.textContent = item.product_title;

    const variantEl = el.querySelector('.cart-item__variant');
    if (variantEl) {
      // If it exists, update it. If item has no variant title now, remove it?
      // Simpler: Just update text if valid
      if (item.variant_title && item.variant_title !== 'Default Title') {
        variantEl.textContent = item.variant_title;
      } else {
        variantEl.remove();
      }
    } else if (item.variant_title && item.variant_title !== 'Default Title') {
      // Create it if missing? For now, we assume structure doesn't change wildly.
      // Ideally re-rendering if structural change is needed is safer, but rare.
      // Let's rely on re-render for complex structural changes if we detect them?
      // No, for stability, if variant changes, we should probably just re-render that one item.
      // But for flickering, price and qty are 99% of updates.
    }

    // Prices
    const finalPriceEl = el.querySelector('.cart-item__final-price');
    if (finalPriceEl) {
      const newText = isReward ? 'FREE' : formatMoney(item.final_price);
      if (finalPriceEl.innerHTML !== newText) finalPriceEl.innerHTML = newText;

      finalPriceEl.classList.toggle('cart-item__final-price--sale', hasDiscount);
    }

    const originalPriceEl = el.querySelector('.cart-item__original-price');
    if (hasDiscount) {
      if (!originalPriceEl) {
        // Create it
        const wrapper = el.querySelector('.cart-item__prices');
        if (wrapper) {
          const span = document.createElement('span');
          span.className = 'cart-item__original-price';
          span.innerHTML = formatMoney(originalPrice);
          wrapper.insertBefore(span, wrapper.firstChild);
        }
      } else {
        originalPriceEl.innerHTML = formatMoney(originalPrice);
      }
    } else if (originalPriceEl) {
      originalPriceEl.remove();
    }

    // Discount Badge
    const badgeEl = el.querySelector('.cart-item__discount-badge');
    if (hasDiscount && discountPercent > 0) {
      const text = `(${discountPercent}% OFF)`;
      if (!badgeEl) {
        const wrapper = el.querySelector('.cart-item__prices');
        if (wrapper) {
          const span = document.createElement('span');
          span.className = 'cart-item__discount-badge';
          span.innerHTML = text;
          wrapper.appendChild(span);
        }
      } else {
        if (badgeEl.innerHTML !== text) badgeEl.innerHTML = text;
      }
    } else if (badgeEl) {
      badgeEl.remove();
    }
  }

  function renderCartItem(item, variantData = null) {
    // Use variant data from Product API (includes compare_at_price)
    // The Cart API doesn't include compare_at_price, so we fetch it separately
    let compareAtPrice = null;

    if (variantData && variantData.compare_at_price) {
      // compare_at_price from Product API is in dollars, convert to cents
      compareAtPrice = Math.round(parseFloat(variantData.compare_at_price) * 100);
    }

    // Calculate the original price and discount
    let originalPrice = item.original_price;
    let hasDiscount = false;
    let discountPercent = 0;

    // Priority 1: Use compare_at_price from Product API if available and higher
    if (compareAtPrice && compareAtPrice > item.final_price) {
      originalPrice = compareAtPrice;
      hasDiscount = true;
      discountPercent = Math.round((compareAtPrice - item.final_price) / compareAtPrice * 100);
    }
    // Priority 2: Check if original_price is higher than final_price (automatic discounts)
    else if (item.original_price > item.final_price) {
      originalPrice = item.original_price;
      hasDiscount = true;
      discountPercent = Math.round((item.original_price - item.final_price) / item.original_price * 100);
    }

    const isReward = item.properties?._reward_tier || item.properties?._is_free_gift;

    // Force discount display for rewards
    if (isReward) {
      hasDiscount = true;
      originalPrice = item.original_price; // Or compareAtPrice if you prefer showing MSRP
      if (compareAtPrice && compareAtPrice > item.original_price) {
        originalPrice = compareAtPrice;
      }
      discountPercent = 100;
    }

    return `
      <div class="cart-item ${isReward ? 'cart-item--reward' : ''}" 
           data-cart-item
           data-line-key="${item.key}"
           data-variant-id="${item.variant_id}"
           data-product-id="${item.product_id}"
           data-compare-at-price="${originalPrice}"
           data-final-price="${item.final_price}"
           data-has-discount="${hasDiscount}"
           data-discount-percent="${discountPercent}">
        
        <div class="cart-item__image-wrapper">
          <a href="${item.url}" class="cart-item__image-link">
            ${item.image
        ? `<img src="${getSizedImageUrl(item.image, '160x')}" alt="${escapeHtml(item.title)}" class="cart-item__image" loading="lazy">`
        : `<div class="cart-item__no-image"><svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1"><rect x="3" y="3" width="18" height="18" rx="2"/></svg></div>`
      }
          </a>
          ${isReward ? '<span class="cart-item__reward-badge">🎁 FREE</span>' : ''}
        </div>
        
        <div class="cart-item__details">
          <a href="${item.url}" class="cart-item__title">${escapeHtml(item.product_title)}</a>
          ${item.variant_title && item.variant_title !== 'Default Title'
        ? `<div class="cart-item__variant">${escapeHtml(item.variant_title)}</div>`
        : ''
      }
          
          <div class="cart-item__price-row">
            <div class="cart-item__prices">
              ${hasDiscount ? `<span class="cart-item__original-price">${formatMoney(originalPrice)}</span>` : ''}
              <span class="cart-item__final-price ${hasDiscount ? 'cart-item__final-price--sale' : ''}">
                ${isReward ? 'FREE' : formatMoney(item.final_price)}
              </span>
              ${hasDiscount && discountPercent > 0 ? `<span class="cart-item__discount-badge">(${discountPercent}% OFF)</span>` : ''}
            </div>
          </div>
          
          <div class="cart-item__actions">
            ${!isReward ? (() => {
        const lockedQty = getLockedQty(item);
        const isLocked = lockedQty > 0 && item.quantity <= lockedQty;
        return `
              <div class="cart-item__quantity">
                <button class="cart-item__qty-btn cart-item__qty-btn--minus${isLocked ? ' cart-item__qty-btn--locked' : ''}" data-qty-decrease aria-label="Decrease quantity"${isLocked ? ' disabled title="Minimum quantity: ' + lockedQty + '"' : ''}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                    <line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                </button>
                <input type="number" class="cart-item__qty-input" value="${item.quantity}" min="${lockedQty || 1}" data-qty-input aria-label="Quantity">
                <button class="cart-item__qty-btn cart-item__qty-btn--plus" data-qty-increase aria-label="Increase quantity">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                    <line x1="12" y1="5" x2="12" y2="19"/>
                    <line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                </button>
              </div>
            `})() : ''}
            
            <button class="cart-item__remove" data-remove-item aria-label="Remove item">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="3 6 5 6 21 6"/>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    `;
  }

  // ============================================
  // TIER PROGRESS SYSTEM
  // ============================================
  // Calculate total excluding rewards/free gifts
  function getEffectiveCartTotal() {
    if (!state.cart) return 0;

    return state.cart.items.reduce((total, item) => {
      // Exclude items that are rewards or free gifts
      if (item.properties?._reward_tier || item.properties?._is_free_gift) {
        return total;
      }
      return total + item.final_line_price;
    }, 0);
  }

  function isTierUnlocked(tier, cart) {
    if (!tier || !cart) return false;

    // Check monetary threshold first
    const cartTotal = getEffectiveCartTotal();
    const thresholdMet = cartTotal >= tier.threshold;

    // If Buy X Get Y, also enforce the "Buy X" product presence AND quantity
    if (tier.offerType === 'buy_x_get_y') {
      if (!thresholdMet) return false;

      const buyXQty = cart.items.reduce((sum, item) => {
        return item.product_id === tier.buyXProductId ? sum + item.quantity : sum;
      }, 0);

      return buyXQty >= (tier.buyXMinQty || 1);
    }

    return thresholdMet;
  }

  // ============================================
  // TIER PROGRESS SYSTEM
  // ============================================
  function updateTierProgress() {
    if (!state.cart || !state.config?.tiers?.length) return;

    // Use effective total (excluding rewards) for progress calculation
    const cartTotal = getEffectiveCartTotal();
    const tiers = state.config.tiers.sort((a, b) => a.threshold - b.threshold);
    const tierCount = tiers.length;

    // Calculate position percentages that match the Liquid template
    // These must match the tier-marker positions from cart-slider.liquid
    const getTierPosition = (index, count) => {
      if (count === 1) return 100;
      if (count === 2) return index === 0 ? 50 : 100;
      if (count === 3) {
        if (index === 0) return 33;
        if (index === 1) return 66;
        return 100;
      }
      // Default fallback: evenly spaced
      return ((index + 1) * 100) / count;
    };

    // Calculate progress percentage based on tier positions
    let progressPercent = 0;

    // Find where we are between tiers
    for (let i = 0; i < tierCount; i++) {
      const tier = tiers[i];
      const tierPosition = getTierPosition(i, tierCount);
      const prevPosition = i === 0 ? 0 : getTierPosition(i - 1, tierCount);
      const prevThreshold = i === 0 ? 0 : tiers[i - 1].threshold;

      if (cartTotal >= tier.threshold) {
        // Passed this tier - fill up to this tier's position
        progressPercent = tierPosition;
      } else if (cartTotal > prevThreshold) {
        // Between previous tier and this tier - calculate proportional fill
        const thresholdRange = tier.threshold - prevThreshold;
        const positionRange = tierPosition - prevPosition;
        const amountIntoRange = cartTotal - prevThreshold;
        const percentIntoRange = amountIntoRange / thresholdRange;
        progressPercent = prevPosition + (positionRange * percentIntoRange);
        break;
      } else {
        break;
      }
    }

    // Clamp to 0-100
    progressPercent = Math.max(0, Math.min(progressPercent, 100));

    // Update progress bar fill
    if (elements.tierFill) {
      elements.tierFill.style.width = `${progressPercent}%`;
    }

    // Track if any tier was newly unlocked for confetti
    let newTierUnlocked = false;

    // Update tier markers
    elements.tierMarkers?.forEach(marker => {
      const threshold = parseInt(marker.dataset.threshold, 10) * 100; // Convert to cents
      const tierIndex = parseInt(marker.dataset.tierIndex, 10);
      const tier = tiers.find(t => t.index === tierIndex);
      const isUnlocked = isTierUnlocked(tier, state.cart);
      const wasAlreadyUnlocked = state.unlockedTiers.has(tierIndex.toString()) || state.unlockedTiers.has(tierIndex);

      marker.classList.toggle(CONFIG.classes.unlocked, isUnlocked);

      // Also update inline styles on the icon element (since Liquid sets inline styles server-side)
      const iconEl = marker.querySelector('.tier-marker__icon');
      if (iconEl) {
        if (isUnlocked) {
          // Get checkpoint color from CSS variable or use default
          const checkpointColor = getComputedStyle(document.querySelector('[data-cart-slider]')).getPropertyValue('--cart-checkpoint-color').trim() || '#ffd700';
          iconEl.style.background = `linear-gradient(135deg, ${checkpointColor}, ${checkpointColor})`;
          iconEl.style.borderColor = checkpointColor;
        } else {
          // Reset to default unlocked styles
          iconEl.style.background = '#ffffff';
          iconEl.style.borderColor = '#e0e0e0';
        }

        // Toggle active/inactive custom icons
        const inactiveIcon = iconEl.querySelector('.tier-icon-inactive');
        const activeIcon = iconEl.querySelector('.tier-icon-active');
        if (inactiveIcon) {
          inactiveIcon.classList.toggle('tier-icon--hidden', isUnlocked && !!activeIcon);
        }
        if (activeIcon) {
          activeIcon.classList.toggle('tier-icon--hidden', !isUnlocked);
        }
      }

      if (isUnlocked && !wasAlreadyUnlocked) {
        // This tier was just unlocked!
        state.unlockedTiers.add(tierIndex);
        newTierUnlocked = true;
      } else if (!isUnlocked && wasAlreadyUnlocked) {
        // This tier is no longer unlocked - remove from tracked unlocked tiers
        state.unlockedTiers.delete(tierIndex);
        state.unlockedTiers.delete(tierIndex.toString());
      }
    });

    // Trigger confetti if any tier was newly unlocked
    if (newTierUnlocked && state.config.animations?.confetti) {
      triggerConfetti();
    }

    // Update tier message
    updateTierMessage(cartTotal, tiers);
  }

  function updateTierMessage(cartTotal, tiers) {
    if (!elements.tierMessage) return;

    // Find first LOCKED tier
    const nextTier = tiers.find(tier => !isTierUnlocked(tier, state.cart));

    if (nextTier) {
      // Special message for Buy X Get Y
      if (nextTier.offerType === 'buy_x_get_y') {
        // If threshold not met, show standard 'Add more' message
        if (cartTotal < nextTier.threshold) {
          const amountNeeded = nextTier.threshold - cartTotal;
          elements.tierMessage.innerHTML = `<span>Add <strong>${formatMoney(amountNeeded)}</strong> more to unlock <strong>${nextTier.label}</strong></span>`;
        } else {
          // Threshold met, but check quantity
          const buyXQty = state.cart.items.reduce((sum, item) => {
            return item.product_id === nextTier.buyXProductId ? sum + item.quantity : sum;
          }, 0);

          const needed = (nextTier.buyXMinQty || 1) - buyXQty;

          if (buyXQty === 0) {
            // None in cart
            elements.tierMessage.innerHTML = `<span>Add <strong>${nextTier.buyXMinQty || 1}x ${nextTier.buyXTitle || 'required product'}</strong> to unlock <strong>${nextTier.label}</strong></span>`;
          } else {
            // Some in cart, need more
            elements.tierMessage.innerHTML = `<span>Add <strong>${needed} more ${nextTier.buyXTitle || 'item'}</strong> to unlock <strong>${nextTier.label}</strong></span>`;
          }
        }
      } else {
        // Standard threshold message
        const amountNeeded = nextTier.threshold - cartTotal;
        elements.tierMessage.innerHTML = `
          <span>Add <strong>${formatMoney(amountNeeded)}</strong> more to unlock <strong>${nextTier.label}</strong></span>
        `;
      }
    } else if (tiers.length > 0) {
      elements.tierMessage.innerHTML = `
        <span>🎉 Congratulations! You've unlocked all rewards!</span>
      `;
    }
  }

  // ============================================
  // TIER REWARDS LOGIC
  // ============================================
  function checkTierRewards() {
    if (!state.cart || !state.config?.tiers?.length) return;

    const effectiveTotal = getEffectiveCartTotal();

    // Helper to check immediate rewards with the correct total
    checkImmediateRewards(effectiveTotal);

    // Check for locked rewards to REMOVE
    state.config.tiers.forEach(tier => {
      const isUnlocked = isTierUnlocked(tier, state.cart);
      const tierKey = `tier_${tier.index}`;

      // Remove reward if cart total drops below threshold
      const rewardInCart = state.cart.items.some(item =>
        item.properties?._reward_tier === tierKey
      );

      if (!isUnlocked && rewardInCart) {
        removeRewardFromCart(tierKey);
        // Clear from processedTiers so the modal/auto-add can trigger again when threshold is met
        if (state.processedTiers) {
          state.processedTiers.delete(tierKey);
        }
      }

      // Also clear processedTiers if threshold is not met (even if reward is not in cart)
      // This ensures modal can re-open if user adds products again
      if (!isUnlocked && state.processedTiers) {
        state.processedTiers.delete(tierKey);
      }
    });
  }

  function checkImmediateRewards(cartTotal) {
    if (!state.cart || !state.config?.tiers?.length) return;

    // Use passed total or calculate effective total
    const total = cartTotal !== undefined ? cartTotal : getEffectiveCartTotal();
    let anyTierUnlocked = false;

    state.config.tiers.forEach(tier => {
      const isUnlocked = isTierUnlocked(tier, state.cart);

      if (isUnlocked) {
        anyTierUnlocked = true;
        const tierKey = `tier_${tier.index}`;

        // Check if reward in cart
        const rewardInCart = state.cart.items.some(item =>
          item.properties?._reward_tier === tierKey
        );

        // If unlocked, free product type OR buy_x_get_y, NOT in cart -> POPUP or Auto-Add
        if (!rewardInCart && (tier.offerType === 'free_product' || tier.offerType === 'buy_x_get_y')) {
          handleFreeProductReward(tier);
        }
      }
    });

    // Show Badge if ANY tier is unlocked AND cart is closed
    if (anyTierUnlocked && elements.tierBadge && !state.isOpen) {
      elements.tierBadge.style.display = 'flex';
    }
  }

  function handleFreeProductReward(tier) {
    const products = tier.productVariants || [];

    if (products.length === 0) return;

    // Check if we've already processed this tier in this session
    const tierKey = `tier_${tier.index}`;
    if (state.processedTiers && state.processedTiers.has(tierKey)) return;
    if (!state.processedTiers) state.processedTiers = new Set();
    state.processedTiers.add(tierKey);

    if (products.length === 1) {
      // Single product - auto add
      const product = products[0];
      addRewardToCartDirect(product.variantId, tier.index);
      if (state.config.animations?.confetti) {
        triggerConfetti();
      }
      showNotification(tier.unlockMessage || `🎉 ${tier.label} unlocked!`, 'success');
    } else {
      // Multiple products - show selection modal
      state.pendingRewardTier = tier;
      showRewardModalDirect(products, tier);
    }
  }

  async function addRewardToCartDirect(variantId, tierIndex) {
    try {
      if (!variantId) return;


      const success = await addToCart({
        id: variantId,
        quantity: 1,
      }, {
        _reward_tier: `tier_${tierIndex}`,
        _is_free_gift: 'true'
      }, { force: true });

      if (!success) {
        console.error('[CartSlider] Failed to auto-add reward (addToCart returned false)');
        // Remove from processed tiers so we can try again
        if (state.processedTiers) {
          state.processedTiers.delete(`tier_${tierIndex}`);
        }
      }

    } catch (error) {
      console.error('Failed to add reward:', error);
    }
  }

  // ============================================
  // CHECKOUT VALIDATION RULES
  // ============================================
  function validateCheckoutRules() {
    if (!state.cart || !state.config?.tiers?.length) return;

    const checkoutBtn = document.querySelector('[data-checkout-btn]');
    if (!checkoutBtn) return;

    // Reset state
    checkoutBtn.style.opacity = '1';
    checkoutBtn.style.pointerEvents = 'auto';
    checkoutBtn.innerHTML = `
        <div class="checkout-btn__content">
            <span class="checkout-btn__text">${state.config.checkoutText || 'Checkout'}</span>
             ${state.config.checkoutSubtext ? `<div class="checkout-btn__subtext-wrapper"><span class="checkout-btn__subtext">${state.config.checkoutSubtext}</span></div>` : ''}
        </div>`;

    // Remove any existing error message
    const existingError = document.querySelector('.cart-slider__error-msg');
    if (existingError) existingError.remove();

    // Find any violations
    let blockingRule = null;

    state.config.tiers.forEach(tier => {
      if (tier.offerType === 'buy_x_get_y' && tier.enforceMinQty) {
        // Check if they have the Buy X product
        const buyXQty = state.cart.items.reduce((sum, item) => {
          return item.product_id === tier.buyXProductId ? sum + item.quantity : sum;
        }, 0);

        if (buyXQty > 0 && buyXQty < tier.buyXMinQty) {
          blockingRule = tier;
        }
      }
    });

    if (blockingRule) {
      // Block Checkout
      checkoutBtn.style.opacity = '0.5';
      checkoutBtn.style.pointerEvents = 'none';

      const needed = blockingRule.buyXMinQty - state.cart.items.reduce((sum, item) => item.product_id === blockingRule.buyXProductId ? sum + item.quantity : sum, 0);

      const errorMsg = document.createElement('div');
      errorMsg.className = 'cart-slider__error-msg';
      errorMsg.style.cssText = 'color: #ff4d4d; font-size: 13px; text-align: center; margin-top: 10px; font-weight: 600;';
      errorMsg.textContent = `You must add ${needed} more ${blockingRule.buyXTitle || 'item'} to checkout.`;

      checkoutBtn.parentNode.insertBefore(errorMsg, checkoutBtn);
    }
  }

  async function addRewardToCart(productHandle, tierIndex) {
    try {
      // Fetch product data to get variant ID
      const response = await fetch(`/products/${productHandle}.js`);
      const product = await response.json();
      const variantId = product.variants[0]?.id;

      if (!variantId) return;


      const success = await addToCart({
        id: variantId,
        quantity: 1,
      }, {
        _reward_tier: `tier_${tierIndex}`,
        _is_free_gift: 'true'
      }, { force: true });

      if (!success) {
        console.error('[CartSlider] Failed to auto-add reward (addToCart returned false)');
        // Remove from processed tiers so we can try again
        if (state.processedTiers) {
          state.processedTiers.delete(`tier_${tierIndex}`);
        }
      }

    } catch (error) {
      console.error('Failed to add reward:', error);
    }
  }

  async function removeRewardFromCart(tierKey) {
    const rewardItem = state.cart.items.find(item =>
      item.properties?._reward_tier === tierKey
    );

    if (rewardItem) {
      await removeItem(rewardItem.key);
    }
  }

  // ============================================
  // REWARD MODAL
  // ============================================
  function showRewardModal(products, tier) {
    if (!elements.rewardModal || !elements.rewardModalBody) return;

    state.selectedReward = null;
    elements.rewardConfirm?.setAttribute('disabled', '');

    // Fetch and render products
    Promise.all(products.map(handle =>
      fetch(`/products/${handle}.js`).then(r => r.json()).catch(() => null)
    )).then(productsData => {
      elements.rewardModalBody.innerHTML = productsData
        .filter(Boolean)
        .map(product => `
          <div class="reward-modal__item" data-product-handle="${product.handle}" data-variant-id="${product.variants[0]?.id}">
            <img src="${getSizedImageUrl(product.featured_image, '120x')}" alt="${escapeHtml(product.title)}" class="reward-modal__item-image">
            <div class="reward-modal__item-info">
              <div class="reward-modal__item-title">${escapeHtml(product.title)}</div>
              <div class="reward-modal__item-price">FREE</div>
            </div>
            <div class="reward-modal__item-check">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </div>
          </div>
        `).join('');

      elements.rewardModal.classList.add(CONFIG.classes.open);
      elements.rewardModal.setAttribute('aria-hidden', 'false');
    });
  }

  function showRewardModalDirect(products, tier) {
    // Re-query elements if not cached (they might not exist at initialization)
    if (!elements.rewardModal) {
      elements.rewardModal = document.querySelector(CONFIG.selectors.rewardModal);
    }
    if (!elements.rewardModalBody) {
      elements.rewardModalBody = document.querySelector(CONFIG.selectors.rewardModalBody);
    }
    if (!elements.rewardConfirm) {
      elements.rewardConfirm = document.querySelector(CONFIG.selectors.rewardConfirm);
    }

    if (!elements.rewardModal || !elements.rewardModalBody) {
      console.warn('[CartSlider] Could not find reward modal elements - modal cannot open');
      return;
    }

    state.selectedReward = null;
    elements.rewardConfirm?.setAttribute('disabled', '');

    // Use pre-loaded product data
    elements.rewardModalBody.innerHTML = products
      .filter(p => p && p.variantId)
      .map(product => `
        <div class="reward-modal__item" data-product-handle="${product.handle}" data-variant-id="${product.variantId}">
          <img src="${product.image || ''}" alt="${escapeHtml(product.title)}" class="reward-modal__item-image" onerror="this.style.display='none'">
          <div class="reward-modal__item-info">
            <div class="reward-modal__item-title">${escapeHtml(product.title)}</div>
            <div class="reward-modal__item-price">FREE</div>
          </div>
          <div class="reward-modal__item-check">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </div>
        </div>
      `).join('');

    elements.rewardModal.classList.add(CONFIG.classes.open);
    elements.rewardModal.setAttribute('aria-hidden', 'false');
  }

  function handleRewardSelect(e) {
    const item = e.target.closest('.reward-modal__item');
    if (!item) return;

    // Remove selection from others
    elements.rewardModalBody.querySelectorAll('.reward-modal__item').forEach(el => {
      el.classList.remove(CONFIG.classes.selected);
    });

    // Select this one
    item.classList.add(CONFIG.classes.selected);
    state.selectedReward = {
      handle: item.dataset.productHandle,
      variantId: item.dataset.variantId
    };

    elements.rewardConfirm?.removeAttribute('disabled');
  }

  async function confirmRewardSelection() {
    if (!state.selectedReward || !state.pendingRewardTier) return;

    closeRewardModal();

    // Use variant ID directly
    await addRewardToCartDirect(state.selectedReward.variantId, state.pendingRewardTier.index);

    if (state.config.animations?.confetti) {
      triggerConfetti();
    }

    showNotification(state.pendingRewardTier.unlockMessage || '🎉 Free gift added!', 'success');

    state.selectedReward = null;
    state.pendingRewardTier = null;
  }

  function closeRewardModal() {
    if (elements.rewardModal) {
      elements.rewardModal.classList.remove(CONFIG.classes.open);
      elements.rewardModal.setAttribute('aria-hidden', 'true');
    }
  }

  // ============================================
  // DISCOUNTED OFFERS
  // ============================================
  function updateDiscountedOffers() {
    if (!state.cart) return;

    const cartTotal = state.cart.total_price;
    const cartVariantIds = state.cart.items.map(item => String(item.variant_id));

    document.querySelectorAll(CONFIG.selectors.discountedOffer).forEach(offer => {
      const minCart = parseInt(offer.dataset.minCart, 10) * 100;
      const variantId = offer.dataset.variantId;
      const checkbox = offer.querySelector('[data-offer-checkbox]');
      const label = offer.querySelector('.discounted-offer__label');

      // Check if cart meets threshold
      const meetsThreshold = cartTotal >= minCart;

      // Check if product is already in cart
      const isInCart = cartVariantIds.includes(variantId);

      // Hide completely if threshold not met
      offer.classList.toggle('discounted-offer--hidden', !meetsThreshold);

      // Show "Added" state if product is in cart
      offer.classList.toggle('discounted-offer--added', isInCart);

      if (checkbox) {
        checkbox.checked = isInCart;
        checkbox.disabled = isInCart; // Disable if already added
      }

      if (label) {
        label.classList.toggle('discounted-offer__label--disabled', isInCart);
      }
    });
  }

  async function handleDiscountedOfferChange(e) {
    const checkbox = e.target;
    const offer = checkbox.closest(CONFIG.selectors.discountedOffer);
    if (!offer) return;

    const variantId = offer.dataset.variantId;
    const specialPrice = parseInt(offer.dataset.specialPrice, 10);

    if (checkbox.checked) {
      // Add product at special price (using line item properties for backend processing)
      await addToCart({
        id: variantId,
        quantity: 1,
      }, {
        _special_price: specialPrice,
        _is_discounted_offer: 'true'
      });
    } else {
      // Find and remove the offer item
      const offerItem = state.cart.items.find(item =>
        item.variant_id == variantId && item.properties?._is_discounted_offer
      );
      if (offerItem) {
        await removeItem(offerItem.key);
      }
    }
  }

  // Keyed Debounce utility (used only for manual input field changes, not button clicks)
  const debounceMap = new Map();

  function debouncedUpdateQuantity(key, qty) {
    // Clear existing timeout for this specific item
    if (debounceMap.has(key)) {
      clearTimeout(debounceMap.get(key));
    }

    const timeout = setTimeout(() => {
      updateItemQuantity(key, qty);
      debounceMap.delete(key);
    }, 150);

    debounceMap.set(key, timeout);
  }

  // ============================================
  // UPSELL HANDLERS
  // ============================================
  function handleUpsellClick(e) {
    const addBtn = e.target.closest(CONFIG.selectors.upsellAdd);
    if (!addBtn || addBtn.disabled) return;

    const variantId = addBtn.dataset.variantId;
    if (!variantId) return;

    // Visual feedback
    addBtn.classList.add(CONFIG.classes.loading);

    addToCart({ id: variantId, quantity: 1 }).then(success => {
      addBtn.classList.remove(CONFIG.classes.loading);

      if (success) {
        addBtn.classList.add(CONFIG.classes.added);
        setTimeout(() => {
          addBtn.classList.remove(CONFIG.classes.added);
        }, 2000);
      }
    });
  }

  /**
   * Handle variant dropdown change on upsell cards
   * Updates prices, image, badge, and variant ID on the card
   */
  function handleUpsellVariantChange(e) {
    const select = e.target.closest('[data-upsell-variant-select]');
    if (!select) return;

    const card = select.closest('[data-upsell-card]');
    if (!card) return;

    const selectedOption = select.options[select.selectedIndex];
    if (!selectedOption) return;

    const newVariantId = select.value;
    const newPrice = parseInt(selectedOption.dataset.price, 10);
    const newCompareAt = parseInt(selectedOption.dataset.compareAtPrice, 10);
    const newImage = selectedOption.dataset.image;

    // Update variant IDs
    card.dataset.variantId = newVariantId;
    const addBtn = card.querySelector('[data-upsell-add]');
    if (addBtn) {
      addBtn.dataset.variantId = newVariantId;
      addBtn.disabled = false;
      addBtn.classList.remove(CONFIG.classes.added);
    }

    // Update prices
    const pricesContainer = card.querySelector('[data-upsell-prices]');
    if (pricesContainer) {
      const hasDiscount = newCompareAt > newPrice;
      const discountPercent = hasDiscount ? Math.round((newCompareAt - newPrice) * 100 / newCompareAt) : 0;

      let priceHtml = '';
      if (hasDiscount) {
        priceHtml += `<span class="upsell-card__original-price" data-upsell-compare-price>${formatMoney(newCompareAt)}</span>`;
      }
      priceHtml += `<span class="upsell-card__final-price" data-upsell-final-price>${formatMoney(newPrice)}</span>`;
      pricesContainer.innerHTML = priceHtml;

      // Update badge
      const badgeEl = card.querySelector('.upsell-card__badge');
      if (badgeEl) {
        if (hasDiscount) {
          badgeEl.textContent = discountPercent + '% off';
          badgeEl.classList.add('upsell-card__badge--discount');
          badgeEl.closest('.upsell-card__badge-wrapper').style.display = '';
        } else {
          badgeEl.closest('.upsell-card__badge-wrapper').style.display = 'none';
        }
      }
    }

    // Update image
    if (newImage) {
      const img = card.querySelector('[data-upsell-image]');
      if (img) {
        img.src = newImage;
      }
    }
  }

  /**
   * Handle Frequently Bought Together add-to-cart clicks
   * Uses data-fbt-add-to-cart buttons with data-variant-id attributes (no form needed).
   * This avoids race conditions with the theme's native form submission handler.
   */
  async function handleFbtClick(e) {
    const btn = e.target.closest('[data-fbt-add-to-cart]');
    if (!btn) return;

    if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;

    e.preventDefault();
    e.stopPropagation();

    const variantId = parseInt(btn.dataset.variantId, 10);
    if (!variantId || isNaN(variantId)) {
      console.error('[FBT] Invalid or missing variant ID on button.');
      return;
    }

    // Open the cart slider immediately in a loading state for instant UI response
    if (!state.isOpen) {
      open(true); // Open but skip immediate fetch
    }

    // Visual loading state
    btn.setAttribute('aria-disabled', 'true');
    btn.classList.add('btn--loading');

    try {
      await addToCart({ id: variantId, quantity: 1 });
    } catch (err) {
      console.error('[FBT] Add to cart error:', err);
    } finally {
      btn.removeAttribute('aria-disabled');
      btn.classList.remove('btn--loading');
    }
  }

  function scrollUpsells(direction) {
    if (!elements.upsellsTrack) return;

    const cardWidth = elements.upsellsTrack.querySelector('.upsell-card')?.offsetWidth || 140;
    const scrollAmount = cardWidth + 12; // card width + gap

    elements.upsellsTrack.scrollBy({
      left: scrollAmount * direction,
      behavior: 'smooth'
    });
  }



  // ============================================
  // CONFETTI ANIMATION
  // ============================================
  function triggerConfetti() {
    // Always re-query confetti container to handle DOM changes from navigation\n    const confettiContainer = document.querySelector(CONFIG.selectors.confetti);\n    if (!confettiContainer) return;

    const colors = ['#ffd700', '#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#ffeaa7'];
    const confettiCount = 50;

    for (let i = 0; i < confettiCount; i++) {
      const confetti = document.createElement('div');
      confetti.className = 'confetti-piece';
      confetti.style.cssText = `
        left: ${Math.random() * 100}%;
        background: ${colors[Math.floor(Math.random() * colors.length)]};
        transform: rotate(${Math.random() * 360}deg);
        animation-delay: ${Math.random() * 0.5}s;
        animation-duration: ${2 + Math.random() * 2}s;
      `;

      confettiContainer.appendChild(confetti);

      // Remove after animation
      setTimeout(() => confetti.remove(), 4000);
    }
  }

  // ============================================
  // UTILITY FUNCTIONS
  // ============================================
  function setLoading(isLoading) {
    state.isLoading = isLoading;
    elements.slider?.classList.toggle(CONFIG.classes.loading, isLoading);

    // Safety net: Auto-clear loading state after 5 seconds to prevent "stuck" UI
    if (isLoading) {
      if (state.loadingTimeout) clearTimeout(state.loadingTimeout);
      state.loadingTimeout = setTimeout(() => {
        if (state.isLoading) {
          console.warn('[CartSlider] Loading state stuck, auto-clearing');
          setLoading(false);
        }
      }, 5000);
    }
  }

  function formatMoney(cents) {
    const format = state.config?.moneyFormat || '{{amount}}';
    const amount = (cents / 100).toFixed(2);

    // Simple format replacement
    return format
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', Math.round(cents / 100))
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ','))
      .replace(/,/g, '');
  }

  function showNotification(message, type = 'info') {

    // Create notification element
    const notification = document.createElement('div');
    notification.className = `cart-notification cart-notification--${type}`;
    notification.innerHTML = `
      <span>${message}</span>
      <button class="cart-notification__close">&times;</button>
    `;

    notification.style.cssText = `
      position: fixed !important;
      top: 70px !important;
      left: 50% !important;
      transform: translateX(-50%) !important;
      padding: 14px 24px !important;
      background: ${type === 'success' ? '#27ae60' : type === 'error' ? '#e74c3c' : type === 'warning' ? '#f39c12' : '#3498db'} !important;
      color: white !important;
      border-radius: 10px !important;
      box-shadow: 0 6px 20px rgba(0,0,0,0.4) !important;
      z-index: 2147483647 !important;
      display: flex !important;
      align-items: center !important;
      gap: 12px !important;
      font-size: 14px !important;
      font-weight: 600 !important;
      max-width: 90% !important;
      text-align: center !important;
    `;

    // Add keyframes if not already added
    if (!document.getElementById('cart-notif-styles')) {
      const style = document.createElement('style');
      style.id = 'cart-notif-styles';
      style.textContent = `
        @keyframes cartNotifSlideIn { from { transform: translateX(-50%) translateY(-20px); opacity: 0; } to { transform: translateX(-50%) translateY(0); opacity: 1; } }
        @keyframes cartNotifSlideOut { from { transform: translateX(-50%) translateY(0); opacity: 1; } to { transform: translateX(-50%) translateY(-20px); opacity: 0; } }
      `;
      document.head.appendChild(style);
    }

    // Always append to body with fixed positioning
    document.body.appendChild(notification);

    // Close button
    notification.querySelector('.cart-notification__close').addEventListener('click', () => {
      notification.style.animation = 'cartNotifSlideOut 0.3s ease forwards';
      setTimeout(() => notification.remove(), 300);
    });

    // Auto remove
    setTimeout(() => {
      if (notification.parentNode) {
        notification.style.animation = 'cartNotifSlideOut 0.3s ease forwards';
        setTimeout(() => notification.remove(), 300);
      }
    }, 4000);
  }

  // ============================================
  // PUBLIC API
  // ============================================
  window.CartSlider = {
    open,
    close,
    refresh: fetchCart,
    addItem: addToCart,
    reinit: function () {

      cacheElements();
      loadConfig();

      // Rebind cart item events if cartItems exists
      if (elements.cartItems) {
        // Remove old listeners to prevent duplicates
        elements.cartItems.removeEventListener('click', handleCartItemClick);
        elements.cartItems.removeEventListener('change', handleQuantityChange);
        // Add new listeners
        elements.cartItems.addEventListener('click', handleCartItemClick);
        elements.cartItems.addEventListener('change', handleQuantityChange);
      }

      fetchCart();
    }
  };

  // ============================================
  // INITIALIZE ON DOM READY
  // ============================================
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // ============================================
  // HANDLE PAGE NAVIGATION (Shopify AJAX, Turbo, etc.)
  // ============================================
  // Shopify section rendering
  document.addEventListener('shopify:section:load', () => {
    window.CartSlider.reinit();
  });

  // Turbolinks/Turbo (used by some Shopify themes)
  document.addEventListener('turbolinks:load', () => {
    window.CartSlider.reinit();
  });

  document.addEventListener('turbo:load', () => {
    window.CartSlider.reinit();
  });

  // MutationObserver to detect when cart slider is re-added to DOM
  const observer = new MutationObserver((mutations) => {
    // Only reinit if we lost the slider reference AND new nodes contain cart-slider
    if (elements.slider) return;

    for (const mutation of mutations) {
      if (mutation.type !== 'childList' || !mutation.addedNodes.length) continue;

      for (const node of mutation.addedNodes) {
        if (node.nodeType !== 1) continue; // Skip text nodes
        if (node.matches?.('[data-cart-slider]') || node.querySelector?.('[data-cart-slider]')) {
          window.CartSlider.reinit();
          return;
        }
      }
    }
  });

  // Start observing (childList only, no subtree to avoid performance overhead)
  observer.observe(document.body, {
    childList: true
  });

})();
